import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, addDoc, getDocs, query, where } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import db, { storage } from "../Firebase";
import { useAuth } from "../AuthContext";
import { THEMES, FREE_THEMES, CardRenderer, CARD_FONTS } from "../CardThemes";
import UpgradeModal from "./UpgradeModal";

const SOCIAL_PLATFORMS = [
    { key: "linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/in/yourname", color: "#0A66C2", icon: "in" },
    { key: "twitter", label: "Twitter / X", placeholder: "https://twitter.com/yourhandle", color: "#000000", icon: "𝕏" },
    { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/yourname", color: "#E1306C", icon: "IG" },
    { key: "facebook", label: "Facebook", placeholder: "https://facebook.com/yourname", color: "#1877F2", icon: "f" },
    { key: "github", label: "GitHub", placeholder: "https://github.com/yourname", color: "#333333", icon: "GH" },
    { key: "whatsapp", label: "WhatsApp", placeholder: "+91 98765 43210", color: "#25D366", icon: "W" },
    { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/@channel", color: "#FF0000", icon: "▶" },
    { key: "behance", label: "Behance", placeholder: "https://behance.net/yourname", color: "#1769FF", icon: "BE" },
    { key: "dribbble", label: "Dribbble", placeholder: "https://dribbble.com/yourname", color: "#EA4C89", icon: "DR" },
    { key: "custom", label: "Custom Link", placeholder: "https://yourlink.com", color: "#6366f1", icon: "+" },
];

const initialSocials = Object.fromEntries(
    SOCIAL_PLATFORMS.map((p) => [p.key, p.key === "custom" ? { enabled: false, url: "", label: "" } : { enabled: false, url: "" }])
);

const STEPS = ["Basic Info", "Professional", "Links & Social", "Review"];
const DRAFT_KEY = "mpc_create_draft";


export default function Home() {
    const routerLocation = useLocation();


    // Mobile tab state: "form" | "preview"
    const [mobileTab, setMobileTab] = useState("form");
    // Show nudge banner until user has visited preview tab once
    const [showPreviewNudge, setShowPreviewNudge] = useState(true);

    const handlePreviewTab = () => {
        setMobileTab("preview");
        setShowPreviewNudge(false);
    };

    // Restore draft from localStorage on first mount
    const savedDraft = (() => {
        try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; }
    })();

    const [step, setStep] = useState(savedDraft?.step ?? 0);
    const [name, setName] = useState(savedDraft?.name ?? "");
    const [phone, setPhone] = useState(savedDraft?.phone ?? "");
    const [email, setEmail] = useState(savedDraft?.email ?? "");
    const [location, setLocation] = useState(savedDraft?.location ?? "");
    const [designation, setDesignation] = useState(savedDraft?.designation ?? "");
    const [company, setCompany] = useState(savedDraft?.company ?? "");
    const [bio, setBio] = useState(savedDraft?.bio ?? "");
    const [websiteEnabled, setWebsiteEnabled] = useState(savedDraft?.websiteEnabled ?? false);
    const [website, setWebsite] = useState(savedDraft?.website ?? "");
    const [socials, setSocials] = useState(savedDraft?.socials ? { ...initialSocials, ...savedDraft.socials } : initialSocials);
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [cropSrc, setCropSrc] = useState(null);
    const [cropPos, setCropPos] = useState({ x: 0, y: 0 });
    const [cropZoom, setCropZoom] = useState(1);
    const cropImgRef = useRef(null);
    const cropDragRef = useRef(null);
    const [bannerFile, setBannerFile] = useState(null);
    const [bannerPreview, setBannerPreview] = useState(null);
    const [resumeFile, setResumeFile] = useState(null);
    const [resumeName, setResumeName] = useState(savedDraft?.resumeName ?? "");
    const [slug, setSlug] = useState(savedDraft?.slug ?? "");
    const [slugStatus, setSlugStatus] = useState("");
    const [slugManual, setSlugManual] = useState(savedDraft?.slugManual ?? false);
    const [theme, setTheme] = useState(routerLocation.state?.preselectedTheme ?? savedDraft?.theme ?? "dark");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [parsing, setParsing] = useState(false);
    const [parseSuccess, setParseSuccess] = useState(false);
    const [parsedCount, setParsedCount] = useState(0);
    const [dragOver, setDragOver] = useState(false);
    const parseInputRef = useRef(null);
    const slugTimer = useRef(null);
    const navigate = useNavigate();
    const { user, signInWithGoogle, isPremium, premiumPlan, premiumExpiry } = useAuth();
    const [showUpgrade, setShowUpgrade] = useState(false);
    const [promoVideoEnabled, setPromoVideoEnabled] = useState(false);
    const [promoVideoType, setPromoVideoType] = useState("youtube");
    const [promoVideoUrl, setPromoVideoUrl] = useState("");
    const [promoVideoFile, setPromoVideoFile] = useState(null);
    const videoRef = useRef(null);
    const [showVideoModal, setShowVideoModal] = useState(false);

    const SERVER = process.env.NODE_ENV === "production"
        ? "https://myprofilecorner.com/api"
        : "http://localhost:3001";

    const handleAutoFill = async (file) => {
        if (!file || file.type !== "application/pdf") return;
        setParsing(true);
        setParseSuccess(false);
        try {
            const formData = new FormData();
            formData.append("resume", file);
            const res = await fetch(`${SERVER}/parse-resume`, { method: "POST", body: formData });
            if (!res.ok) throw new Error("Parse failed");
            const data = await res.json();

            const SOCIAL_KEYS = ["linkedin", "github", "twitter", "instagram", "whatsapp"];
            let count = 0;
            const fields = [
                [data.name, setName],
                [data.phone, setPhone],
                [data.email, setEmail],
                [data.location, setLocation],
                [data.designation, setDesignation],
                [data.company, setCompany],
                [data.bio, setBio],
                [data.website, (v) => { if (v) { setWebsiteEnabled(true); setWebsite(v); } }],
            ];
            for (let i = 0; i < fields.length; i++) {
                const [val, setter] = fields[i];
                if (val) { await delay(80); setter(val); count++; }
            }
            for (const key of SOCIAL_KEYS) {
                if (data[key]) {
                    await delay(80);
                    setSocials(prev => ({ ...prev, [key]: { enabled: true, url: data[key] } }));
                    count++;
                }
            }
            setParsedCount(count);
            setParseSuccess(true);
        } catch {
            alert("Couldn't read the resume. Please try a different PDF.");
        } finally {
            setParsing(false);
        }
    };

    const delay = (ms) => new Promise(r => setTimeout(r, ms));

    const initials = name.trim()
        ? name.trim().split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
        : "?";

    const enabledSocials = SOCIAL_PLATFORMS.filter(p => socials[p.key].enabled);

    // Save draft to localStorage whenever form data changes
    useEffect(() => {
        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify({
                step, name, phone, email, location, designation, company, bio,
                websiteEnabled, website, socials, resumeName, slug, slugManual, theme,
            }));
        } catch { /* quota exceeded — ignore */ }
    }, [step, name, phone, email, location, designation, company, bio,
        websiteEnabled, website, socials, resumeName, slug, slugManual, theme]);

    useEffect(() => {
        if (routerLocation.state?.preselectedTheme) {
            setTheme(routerLocation.state.preselectedTheme);
        }
    }, [routerLocation.state]);

    useEffect(() => {
        if (user && slug) checkSlug(slug);
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (slugManual) return;
        const auto = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
        setSlug(auto);
        if (auto) checkSlug(auto);
    }, [name]);

    const checkSlug = async (value) => {
        if (!value) { setSlugStatus(""); return; }
        if (!user) { setSlugStatus(""); return; }
        setSlugStatus("checking");
        clearTimeout(slugTimer.current);
        slugTimer.current = setTimeout(async () => {
            try {
                const snap = await getDocs(query(collection(db, "cards"), where("slug", "==", value)));
                setSlugStatus(snap.empty ? "available" : "taken");
            } catch (err) {
                console.error("Slug check failed:", err);
                setSlugStatus("");
                setError("Unable to check URL handle. Please sign in again.");
            }
        }, 500);
    };

    const handleSlugChange = (raw) => {
        const clean = raw.toLowerCase().replace(/[^a-z0-9-]/g, "").replace(/--+/g, "-");
        setSlug(clean);
        setSlugManual(true);
        checkSlug(clean);
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            setCropSrc(ev.target.result);
            setCropPos({ x: 0, y: 0 });
            setCropZoom(1);
        };
        reader.readAsDataURL(file);
        e.target.value = "";
    };

    const confirmCrop = () => {
        const img = cropImgRef.current;
        if (!img) return;
        const viewport = 240; // matches the circular crop UI size
        const output = 400;
        const srcX = img.naturalWidth / 2 - viewport / (2 * cropZoom) - cropPos.x / cropZoom;
        const srcY = img.naturalHeight / 2 - viewport / (2 * cropZoom) - cropPos.y / cropZoom;
        const srcW = viewport / cropZoom;
        const srcH = viewport / cropZoom;
        const canvas = document.createElement("canvas");
        canvas.width = output;
        canvas.height = output;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, output, output);
        canvas.toBlob((blob) => {
            const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
            setPhotoFile(file);
            setPhotoPreview(canvas.toDataURL("image/jpeg"));
            setCropSrc(null);
        }, "image/jpeg", 0.92);
    };

    const handleBannerChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setBannerFile(file);
        setBannerPreview(URL.createObjectURL(file));
    };

    const handleResumeChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setResumeFile(file);
        setResumeName(file.name);
    };

    const toggleSocial = (key) =>
        setSocials(prev => ({ ...prev, [key]: { ...prev[key], enabled: !prev[key].enabled } }));

    const updateSocialUrl = (key, url) =>
        setSocials(prev => ({ ...prev, [key]: { ...prev[key], url } }));

    const updateSocialLabel = (label) =>
        setSocials(prev => ({ ...prev, custom: { ...prev.custom, label } }));

    const canNext = () => {
        if (step === 0) return name.trim() && phone.trim() && email.trim() && slug && slugStatus === "available";
        return true;
    };

    const handleSubmit = async () => {
        if (slugStatus !== "available") { setError("Please choose an available URL handle."); return; }
        if (!FREE_THEMES.includes(theme) && !isPremium) {
            setShowUpgrade(true);
            return;
        }
        setLoading(true);
        setError("");
        try {
            let photoUrl = "";
            if (photoFile) {
                const photoRef = ref(storage, `profile-photos/${slug}_${Date.now()}`);
                await uploadBytes(photoRef, photoFile);
                photoUrl = await getDownloadURL(photoRef);
            }
            let bannerUrl = "";
            if (bannerFile) {
                const bannerRef = ref(storage, `banners/${slug}_${Date.now()}`);
                await uploadBytes(bannerRef, bannerFile);
                bannerUrl = await getDownloadURL(bannerRef);
            }
            let resumeUrl = "";
            if (resumeFile) {
                const resumeRef = ref(storage, `resumes/${slug}_${Date.now()}_${resumeFile.name}`);
                await uploadBytes(resumeRef, resumeFile);
                resumeUrl = await getDownloadURL(resumeRef);
            }
            let finalPromoVideoUrl = "";
            let finalPromoVideoType = "";
            if (promoVideoEnabled) {
                if (promoVideoType === "youtube" && promoVideoUrl.trim()) {
                    finalPromoVideoUrl = promoVideoUrl.trim();
                    finalPromoVideoType = "youtube";
                } else if (promoVideoType === "upload" && promoVideoFile) {
                    const videoStorageRef = ref(storage, `promo-videos/${slug}_${Date.now()}_${promoVideoFile.name}`);
                    await uploadBytes(videoStorageRef, promoVideoFile);
                    finalPromoVideoUrl = await getDownloadURL(videoStorageRef);
                    finalPromoVideoType = "upload";
                }
            }
            const socialsToSave = {};
            SOCIAL_PLATFORMS.forEach(({ key }) => {
                if (socials[key].enabled && socials[key].url.trim()) {
                    socialsToSave[key] = socials[key].url.trim();
                    if (key === "custom" && socials.custom.label?.trim())
                        socialsToSave.customLabel = socials.custom.label.trim();
                }
            });
            await addDoc(collection(db, "cards"), {
                slug, name: name.trim(), phone: phone.trim(), email: email.trim(),
                location: location.trim(), designation: designation.trim(),
                company: company.trim(), bio: bio.trim(),
                website: websiteEnabled ? website.trim() : "",
                photoUrl, bannerUrl, resumeUrl, socials: socialsToSave,
                theme,
                promoVideoUrl: finalPromoVideoUrl,
                promoVideoType: finalPromoVideoType,
                userId: user.uid,
                createdAt: new Date(),
            });
            localStorage.removeItem(DRAFT_KEY);
            navigate(`/card/${slug}`);
        } catch (err) {
            console.error("Full error:", err);
            setError(`Error: ${err.code || err.message}`);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!user) return;
        const checkExisting = async () => {
            const snap = await getDocs(query(collection(db, "cards"), where("userId", "==", user.uid)));
            if (!snap.empty) {
                const existing = snap.docs[0].data();
                navigate(`/card/${existing.slug}`, { replace: true });
            }
        };
        checkExisting();
    }, [user]);

    if (user === undefined) {
        return <div style={s.authPage}><p style={{ color: "#fff" }}>Loading...</p></div>;
    }

    if (!user) {
        return (
            <div style={s.authPage}>
                <div style={s.authBox}>
                    <div style={s.authLogo}><span style={s.logoDot}>●</span> MyProfileCorner</div>
                    <h2 style={s.authTitle}>Sign in to create your card</h2>
                    <p style={s.authSub}>We use Google Sign-In to keep your card secure. Only you can edit it.</p>
                    <button onClick={signInWithGoogle} style={s.googleBtn}>
                        <GoogleIcon /> Continue with Google
                    </button>
                    <p style={s.authNote}>Free forever · No spam · Cancel anytime</p>
                </div>
            </div>
        );
    }

    // ─── Shared card data for preview ─────────────────────────────────────────
    const cardPreviewData = {
        name, phone, email, location, designation, company, bio,
        photoPreview, bannerPreview,
        website: websiteEnabled ? website : "",
        resumeUrl: resumeName ? "preview" : "",
        promoVideoUrl: promoVideoEnabled ? promoVideoUrl : "",
        promoVideoType,
        socials: {
            ...Object.fromEntries(
                enabledSocials.filter(p => socials[p.key]?.url).map(p => [p.key, socials[p.key].url])
            ),
            ...(socials.custom?.enabled && socials.custom.label?.trim() ? { customLabel: socials.custom.label.trim() } : {}),
        },
    };

    // ─── Preview panel content (shared between desktop right col and mobile tab) ─
    const PreviewContent = () => (
        <div style={s.previewInner}>
            <ThemeStrip selected={theme} onSelect={setTheme} isPremium={isPremium} onUpgrade={() => setShowUpgrade(true)} />
            <style>{CARD_FONTS}</style>
            <CardRenderer
                themeId={theme}
                cardData={cardPreviewData}
                onVideoClick={(promoVideoEnabled && promoVideoUrl) ? () => setShowVideoModal(true) : undefined}
            />
            <div style={{ paddingBottom: "32px" }} />
        </div>
    );

    // ─── Form panel content ────────────────────────────────────────────────────
    const FormContent = () => (
        <>
            {/* Top bar */}
            <div style={s.topBar}>
                <button onClick={() => navigate("/")} style={s.backBtn}>← Home</button>
                <span style={s.topLogo}><span style={s.logoDot}>●</span> MyProfileCorner</span>
            </div>

            {/* Premium status banner */}
            {isPremium && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 2rem", background: "linear-gradient(135deg, rgba(10,173,161,0.08), rgba(15,143,133,0.05))", borderBottom: "1px solid rgba(10,173,161,0.15)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{ background: "linear-gradient(135deg,#0AADA1,#0f8f85)", borderRadius: "20px", padding: "3px 10px", fontSize: "11px", fontWeight: "800", color: "#fff", letterSpacing: "0.3px" }}>
                            ✨ PREMIUM
                        </div>
                        <span style={{ fontSize: "12px", color: "#0AADA1", fontWeight: "600" }}>
                            {premiumPlan === "yearly" ? "Yearly" : "Monthly"} plan active
                        </span>
                    </div>
                    {premiumExpiry && (
                        <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                            Valid till {new Date(premiumExpiry).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                    )}
                </div>
            )}

            {/* Progress Steps */}
            <div style={s.stepBar}>
                {STEPS.map((label, i) => (
                    <div key={i} style={s.stepItem}>
                        <div style={{
                            ...s.stepCircle,
                            background: i <= step ? "#6366f1" : "#e5e7eb",
                            color: i <= step ? "#fff" : "#9ca3af",
                            boxShadow: i === step ? "0 0 0 4px rgba(99,102,241,0.2)" : "none",
                        }}>
                            {i < step ? "✓" : i + 1}
                        </div>
                        <span style={{
                            ...s.stepLabel,
                            color: i === step ? "#6366f1" : i < step ? "#1a1a2e" : "#9ca3af",
                            fontWeight: i === step ? "700" : "500",
                        }}>{label}</span>
                        {i < STEPS.length - 1 && (
                            <div style={{ ...s.stepLine, background: i < step ? "#6366f1" : "#e5e7eb" }} />
                        )}
                    </div>
                ))}
            </div>

            {/* Form Body */}
            <div style={{ ...s.formBody, paddingBottom: "2rem" }}>

                {/* STEP 0 — Basic Info */}
                {step === 0 && (
                    <div style={s.stepContent}>
                        <h2 style={s.stepHeading}>Let's start with the basics.</h2>
                        <p style={s.stepSub}>Your core contact information — this is what people see first.</p>

                        {/* Mobile: Preview nudge banner */}
                        {showPreviewNudge && (
                            <div style={s.nudgeBanner}>
                                <span style={s.nudgeIcon}>👁</span>
                                <span style={s.nudgeText}>
                                    Fill in your details and tap <strong>Preview</strong> below to see your card live
                                </span>
                                <button style={s.nudgeClose} onClick={() => setShowPreviewNudge(false)}>✕</button>
                            </div>
                        )}

                        {/* AI Resume Auto-fill */}
                        <div
                            style={{
                                ...s.autoFillCard,
                                borderColor: dragOver ? "#0AADA1" : parseSuccess ? "#22c55e" : "rgba(10,173,161,0.3)",
                                background: parseSuccess
                                    ? "linear-gradient(135deg,rgba(34,197,94,0.06),rgba(34,197,94,0.02))"
                                    : dragOver
                                        ? "linear-gradient(135deg,rgba(10,173,161,0.1),rgba(99,102,241,0.06))"
                                        : "linear-gradient(135deg,rgba(10,173,161,0.05),rgba(99,102,241,0.03))",
                                transform: dragOver ? "scale(1.01)" : "scale(1)",
                            }}
                            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={e => { e.preventDefault(); setDragOver(false); handleAutoFill(e.dataTransfer.files[0]); }}
                        >
                            <div style={s.autoFillTopBar} />
                            {parseSuccess ? (
                                <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                                    <div style={s.autoFillSuccessIcon}>✓</div>
                                    <div style={{ flex: 1 }}>
                                        <div style={s.autoFillSuccessTitle}>{parsedCount} fields auto-filled from your resume!</div>
                                        <div style={s.autoFillSuccessSub}>Review below and adjust anything if needed.</div>
                                    </div>
                                    <button style={s.autoFillReparse} onClick={() => parseInputRef.current?.click()}>↺ Re-scan</button>
                                </div>
                            ) : parsing ? (
                                <div style={{ textAlign: "center", padding: "8px 0" }}>
                                    <div style={s.autoFillScanLabel}>🤖 Reading your resume with AI...</div>
                                    <div style={s.autoFillScanBar}><div style={s.autoFillScanFill} /></div>
                                    <div style={s.autoFillScanSub}>Extracting name, contact, experience & links</div>
                                </div>
                            ) : (
                                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                    <div style={s.autoFillIcon}>{dragOver ? "📂" : "✨"}</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={s.autoFillTitle}>Auto-fill from Resume/Portfolio</div>
                                        <div style={s.autoFillSub}>
                                            {dragOver ? "Drop your PDF here!" : "Upload your PDF — AI fills everything in seconds"}
                                        </div>
                                        <button style={{ ...s.autoFillBtn, marginTop: "10px" }} onClick={() => parseInputRef.current?.click()}>Upload PDF</button>
                                    </div>
                                </div>
                            )}
                            <input ref={parseInputRef} type="file" accept="application/pdf"
                                style={{ display: "none" }} onChange={e => handleAutoFill(e.target.files[0])} />
                        </div>

                        {/* Photo Upload */}
                        <div style={s.photoArea}>
                            <div style={s.avatarWrap}>
                                {photoPreview
                                    ? <img src={photoPreview} alt="preview" style={s.avatarImg} />
                                    : <div style={s.avatarInitials}>{initials}</div>
                                }
                                <label htmlFor="photo-input" style={s.cameraBtn}><CameraIcon /></label>
                                <input id="photo-input" type="file" accept="image/*"
                                    onChange={handlePhotoChange} style={{ display: "none" }} />
                            </div>
                            <div>
                                <p style={s.photoTitle}>Profile Photo</p>
                                <p style={s.photoHint}>Appears on your card and Google Wallet pass</p>
                                <label htmlFor="photo-input" style={s.photoUploadBtn}>Upload Photo</label>
                            </div>
                        </div>

                        {/* Banner Upload (dark theme only) */}
                        {theme === "dark" && (
                            <div style={s.bannerArea}>
                                <div style={s.bannerPreviewBox}>
                                    {bannerPreview
                                        ? <img src={bannerPreview} alt="banner" style={s.bannerImg} />
                                        : <span style={s.bannerPlaceholder}>🖼 Cover Image</span>
                                    }
                                </div>
                                <div>
                                    <p style={s.photoTitle}>Cover / Banner Image</p>
                                    <p style={s.photoHint}>Wide image shown at top of dark card (16:7 ratio works best)</p>
                                    <label htmlFor="banner-input" style={s.photoUploadBtn}>Upload Banner</label>
                                    <input id="banner-input" type="file" accept="image/*"
                                        onChange={handleBannerChange} style={{ display: "none" }} />
                                </div>
                            </div>
                        )}

                        <div style={s.fieldGrid}>
                            <Field label="Full Name *" value={name} onChange={setName} placeholder="Enter Full Name" required span={2} />
                            <Field label="Phone Number *" value={phone} onChange={setPhone} placeholder="+91 98********" required />
                            <Field label="Email Address *" type="email" value={email} onChange={setEmail} placeholder="yourid@example.com" required />
                            <Field label="Location" value={location} onChange={setLocation} placeholder="eg : Gurugram, Haryana" span={2} />
                        </div>

                        <div style={s.field}>
                            <label style={s.label}>Your Card URL *</label>
                            <div style={s.slugWrap}>
                                <span style={s.slugBase}>myprofilecorner.com/</span>
                                <input
                                    style={{ ...s.slugInput, borderColor: slugStatus === "available" ? "#22c55e" : slugStatus === "taken" ? "#ef4444" : "#e5e7eb" }}
                                    value={slug}
                                    onChange={(e) => handleSlugChange(e.target.value)}
                                    placeholder="your name"
                                />
                            </div>
                            <div style={s.slugStatus}>
                                {slugStatus === "checking" && <span style={s.slugChecking}>⏳ Checking availability...</span>}
                                {slugStatus === "available" && <span style={s.slugAvailable}>✓ Available — your card will be at <b>myprofilecorner.com/{slug}</b></span>}
                                {slugStatus === "taken" && <span style={s.slugTaken}>✗ Already taken — try a different handle</span>}
                                {!slugStatus && slug && <span style={s.slugChecking}>Type to check availability</span>}
                            </div>
                        </div>
                    </div>
                )}

                {/* STEP 1 — Professional */}
                {step === 1 && (
                    <div style={s.stepContent}>
                        <h2 style={s.stepHeading}>Your professional profile</h2>
                        <p style={s.stepSub}>Help people understand who you are and what you do.</p>
                        <div style={s.fieldGrid}>
                            <Field label="Designation / Role" value={designation} onChange={setDesignation} placeholder="eg : Full Stack Developer" span={2} />
                            <Field label="Company / Organisation" value={company} onChange={setCompany} placeholder="eg : Google, Microsoft" span={2} />
                        </div>
                        <div style={s.field}>
                            <label style={s.label}>Short Bio</label>
                            <textarea
                                style={{ ...s.input, resize: "vertical", minHeight: "100px", paddingTop: "0.75rem" }}
                                placeholder="Tell people a little about yourself — your expertise, passion, or what you're working on..."
                                value={bio}
                                onChange={(e) => setBio(e.target.value)}
                                rows={4}
                            />
                        </div>
                        <div style={s.resumeUploadArea}>
                            <div style={s.resumeIcon}>📄</div>
                            <div style={{ flex: 1 }}>
                                <p style={s.resumeTitle}>Resume / CV</p>
                                <p style={s.resumeHint}>{resumeName || "PDF or DOC — visitors can download it from your card"}</p>
                            </div>
                            <label htmlFor="resume-input" style={s.resumeBtn}>
                                {resumeName ? "Change" : "Upload"}
                            </label>
                            <input id="resume-input" type="file" accept=".pdf,.doc,.docx"
                                onChange={handleResumeChange} style={{ display: "none" }} />
                        </div>
                    </div>
                )}

                {/* STEP 2 — Links & Social */}
                {step === 2 && (
                    <div style={s.stepContent}>
                        <h2 style={s.stepHeading}>Links & Social Media</h2>
                        <p style={s.stepSub}>Add your online presence. Toggle on what you want to show.</p>
                        <div style={s.linkRow}>
                            <div style={s.linkLeft}>
                                <div style={{ ...s.linkIcon, background: "#f0f0ff" }}>🌐</div>
                                <div>
                                    <p style={s.linkLabel}>Website</p>
                                    <p style={s.linkHint}>Your personal or business site</p>
                                </div>
                            </div>
                            <label style={s.toggle}>
                                <input type="checkbox" checked={websiteEnabled}
                                    onChange={() => setWebsiteEnabled(v => !v)} style={{ display: "none" }} />
                                <div style={{ ...s.toggleTrack, background: websiteEnabled ? "#6366f1" : "#e5e7eb" }}>
                                    <div style={{ ...s.toggleThumb, left: websiteEnabled ? "22px" : "2px" }} />
                                </div>
                            </label>
                        </div>
                        {websiteEnabled && (
                            <input style={{ ...s.input, marginBottom: "0.5rem" }}
                                placeholder="https://yourwebsite.com"
                                value={website}
                                onChange={(e) => setWebsite(e.target.value)} />
                        )}
                        <div style={s.divider} />
                        {SOCIAL_PLATFORMS.map(({ key, label, placeholder, color, icon }) => (
                            <div key={key}>
                                <div style={s.linkRow}>
                                    <div style={s.linkLeft}>
                                        <div style={{ ...s.socialBadge, background: color }}>
                                            <span style={{ color: "#fff", fontSize: "0.7rem", fontWeight: "800" }}>{icon}</span>
                                        </div>
                                        <div><p style={s.linkLabel}>{label}</p></div>
                                    </div>
                                    <label style={s.toggle}>
                                        <input type="checkbox" checked={socials[key].enabled}
                                            onChange={() => toggleSocial(key)} style={{ display: "none" }} />
                                        <div style={{ ...s.toggleTrack, background: socials[key].enabled ? color : "#e5e7eb" }}>
                                            <div style={{ ...s.toggleThumb, left: socials[key].enabled ? "22px" : "2px" }} />
                                        </div>
                                    </label>
                                </div>
                                {socials[key].enabled && (
                                    <>
                                        <input style={{ ...s.input, marginBottom: "0.5rem" }}
                                            placeholder={placeholder}
                                            value={socials[key].url}
                                            onChange={(e) => updateSocialUrl(key, e.target.value)} />
                                        {key === "custom" && (
                                            <input style={{ ...s.input, marginBottom: "0.5rem" }}
                                                placeholder="Link name (e.g. Portfolio, Blog…)"
                                                value={socials.custom.label || ""}
                                                onChange={(e) => updateSocialLabel(e.target.value)} />
                                        )}
                                    </>
                                )}
                            </div>
                        ))}

                        {/* Promo Video */}
                        <div style={s.divider} />
                        <div style={s.linkRow}>
                            <div style={s.linkLeft}>
                                <div style={{ ...s.linkIcon, background: "#fff0f0" }}>🎬</div>
                                <div>
                                    <p style={s.linkLabel}>Promo Video</p>
                                    <p style={s.linkHint}>Play button on avatar — YouTube or upload</p>
                                </div>
                            </div>
                            <label style={s.toggle}>
                                <input type="checkbox" checked={promoVideoEnabled}
                                    onChange={() => setPromoVideoEnabled(v => !v)} style={{ display: "none" }} />
                                <div style={{ ...s.toggleTrack, background: promoVideoEnabled ? "#ef4444" : "#e5e7eb" }}>
                                    <div style={{ ...s.toggleThumb, left: promoVideoEnabled ? "22px" : "2px" }} />
                                </div>
                            </label>
                        </div>
                        {promoVideoEnabled && (
                            <>
                                <div style={{ display: "flex", gap: "8px", margin: "8px 0" }}>
                                    <button
                                        type="button"
                                        onClick={() => setPromoVideoType("youtube")}
                                        style={{ flex: 1, padding: "8px", borderRadius: "8px", border: `2px solid ${promoVideoType === "youtube" ? "#ef4444" : "#e5e7eb"}`, background: promoVideoType === "youtube" ? "#fff0f0" : "#fff", color: promoVideoType === "youtube" ? "#ef4444" : "#6b7280", fontWeight: "600", cursor: "pointer", fontSize: "13px", fontFamily: "inherit" }}
                                    >▶ YouTube URL</button>
                                    <button
                                        type="button"
                                        onClick={() => setPromoVideoType("upload")}
                                        style={{ flex: 1, padding: "8px", borderRadius: "8px", border: `2px solid ${promoVideoType === "upload" ? "#ef4444" : "#e5e7eb"}`, background: promoVideoType === "upload" ? "#fff0f0" : "#fff", color: promoVideoType === "upload" ? "#ef4444" : "#6b7280", fontWeight: "600", cursor: "pointer", fontSize: "13px", fontFamily: "inherit" }}
                                    >📤 Upload Video</button>
                                </div>
                                {promoVideoType === "youtube" && (
                                    <input
                                        style={{ ...s.input, marginBottom: "0.5rem" }}
                                        placeholder="https://youtube.com/watch?v=..."
                                        value={promoVideoUrl}
                                        onChange={(e) => setPromoVideoUrl(e.target.value)}
                                    />
                                )}
                                {promoVideoType === "upload" && (
                                    <div style={s.resumeUploadArea}>
                                        <div style={{ fontSize: "1.5rem" }}>🎬</div>
                                        <div style={{ flex: 1 }}>
                                            <p style={s.resumeTitle}>Video File</p>
                                            <p style={s.resumeHint}>{promoVideoFile ? promoVideoFile.name : "MP4, WebM — max 50MB"}</p>
                                        </div>
                                        <label htmlFor="promo-video-input" style={s.resumeBtn}>
                                            {promoVideoFile ? "Change" : "Upload"}
                                        </label>
                                        <input id="promo-video-input" type="file" accept="video/mp4,video/webm,video/*"
                                            ref={videoRef}
                                            onChange={(e) => { const f = e.target.files[0]; if (f) setPromoVideoFile(f); }}
                                            style={{ display: "none" }} />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* STEP 3 — Review */}
                {step === 3 && (
                    <div style={s.stepContent}>
                        <h2 style={s.stepHeading}>Almost there! Review your card</h2>
                        <p style={s.stepSub}>Everything looks good? Hit create and your card goes live instantly.</p>
                        <div style={s.reviewGrid}>
                            <ReviewRow icon="👤" label="Name" value={name} />
                            <ReviewRow icon="📞" label="Phone" value={phone} />
                            <ReviewRow icon="✉️" label="Email" value={email} />
                            {location && <ReviewRow icon="📍" label="Location" value={location} />}
                            {designation && <ReviewRow icon="💼" label="Role" value={designation} />}
                            {company && <ReviewRow icon="🏢" label="Company" value={company} />}
                            {websiteEnabled && website && <ReviewRow icon="🌐" label="Website" value={website} />}
                            {resumeName && <ReviewRow icon="📄" label="Resume" value={resumeName} />}
                            {enabledSocials.length > 0 && (
                                <ReviewRow icon="🔗" label="Social Links" value={`${enabledSocials.length} platform${enabledSocials.length > 1 ? "s" : ""} added`} />
                            )}
                        </div>
                        {error && <p style={s.error}>{error}</p>}
                        <div style={{ display: "flex", gap: "10px", marginBottom: "12px" }}>
                            <button onClick={() => setStep(2)} style={s.backStepBtn}>← Back</button>
                            <button onClick={handleSubmit} style={{ ...s.createBtn, margin: 0, flex: 1 }} disabled={loading}>
                                {loading ? <span>Creating your card...</span> : <span>🚀 Create My Digital Card</span>}
                            </button>
                        </div>
                        <p style={s.reviewNote}>Your card goes live immediately. You can edit it anytime.</p>
                    </div>
                )}

                {/* Navigation */}
                {step < 3 && (
                    <div style={s.navRow}>
                        {step > 0 && (
                            <button onClick={() => setStep(s => s - 1)} style={s.backStepBtn}>← Back</button>
                        )}
                        <button
                            onClick={() => setStep(s => s + 1)}
                            style={{ ...s.nextBtn, opacity: canNext() ? 1 : 0.5 }}
                            disabled={!canNext()}
                        >
                            {step === 2 ? "Review Card →" : "Continue →"}
                        </button>
                    </div>
                )}
            </div>
        </>
    );

    // ─── Single responsive layout (CSS media queries) ─────────────────────────
    return (
        <div className="home-root">
            <style>{`
                @keyframes scan-slide { from { margin-left:0%; } to { margin-left:60%; } }

                html, body { overflow-x: hidden; }

                .home-root {
                    min-height: 100vh;
                    display: grid;
                    grid-template-columns: 1fr 420px;
                    font-family: 'Segoe UI', Arial, sans-serif;
                }
                .home-form-panel {
                    background: #fff;
                    display: flex;
                    flex-direction: column;
                    border-right: 1px solid #f3f4f6;
                    min-height: 100vh;
                }
                .home-preview-panel {
                    background: linear-gradient(160deg, #0f0c29 0%, #1a1a2e 50%, #0f3460 100%);
                    padding: 2rem 1.5rem;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    position: sticky;
                    top: 0;
                    height: 100vh;
                    overflow-y: auto;
                }
                .home-tab-bar { display: none; }

                @media (max-width: 768px) {
                    .home-root {
                        display: flex;
                        flex-direction: column;
                    }
                    .home-form-panel {
                        display: ${mobileTab === "form" ? "flex" : "none"};
                        flex-direction: column;
                        min-height: 100vh;
                        padding-bottom: 80px;
                    }
                    .home-preview-panel {
                        display: ${mobileTab === "preview" ? "flex" : "none"};
                        flex-direction: column;
                        align-items: center;
                        overflow-y: auto;
                        min-height: 100vh;
                        padding: 1.5rem 1rem 90px;
                        position: static;
                        height: auto;
                    }
                    .home-tab-bar {
                        display: flex;
                        position: fixed;
                        bottom: 0; left: 0; right: 0;
                        height: 64px;
                        background: #fff;
                        border-top: 1px solid #e5e7eb;
                        align-items: stretch;
                        z-index: 200;
                        box-shadow: 0 -4px 20px rgba(0,0,0,0.08);
                    }
                }
            `}</style>

            {/* Form Panel */}
            <div className="home-form-panel">
                {FormContent()}
            </div>

            {/* Preview Panel */}
            <div className="home-preview-panel">
                {PreviewContent()}
            </div>

            {/* Mobile Tab Bar */}
            <div className="home-tab-bar">
                <button
                    style={{
                        ...s.mobileTabBtn,
                        color: mobileTab === "form" ? "#6366f1" : "#9ca3af",
                        borderTop: mobileTab === "form" ? "2px solid #6366f1" : "2px solid transparent",
                    }}
                    onClick={() => setMobileTab("form")}
                >
                    <FormTabIcon active={mobileTab === "form"} />
                    <span style={s.mobileTabLabel}>Form</span>
                    <span style={{
                        ...s.mobileTabPill,
                        background: mobileTab === "form" ? "#6366f1" : "#e5e7eb",
                        color: mobileTab === "form" ? "#fff" : "#9ca3af",
                    }}>
                        {step + 1}/{STEPS.length}
                    </span>
                </button>
                <div style={s.mobileTabDivider} />
                <button
                    style={{
                        ...s.mobileTabBtn,
                        color: mobileTab === "preview" ? "#0AADA1" : "#9ca3af",
                        borderTop: mobileTab === "preview" ? "2px solid #0AADA1" : "2px solid transparent",
                        position: "relative",
                    }}
                    onClick={handlePreviewTab}
                >
                    <PreviewTabIcon active={mobileTab === "preview"} />
                    <span style={s.mobileTabLabel}>Preview</span>
                    <span style={s.liveDot} />
                </button>
            </div>

            {/* ── Crop Modal ── */}
            {cropSrc && (
                <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
                    <div style={{ background: "#fff", borderRadius: "24px", padding: "24px 20px 20px", width: "100%", maxWidth: "320px", textAlign: "center" }}>
                        <h3 style={{ margin: "0 0 4px", fontSize: "16px", fontWeight: "700", color: "#111" }}>Crop Photo</h3>
                        <p style={{ margin: "0 0 16px", fontSize: "12px", color: "#9ca3af" }}>Drag to reposition · Scroll to zoom</p>

                        {/* Circular viewport */}
                        <div
                            style={{ width: "240px", height: "240px", borderRadius: "50%", overflow: "hidden", margin: "0 auto 16px", cursor: "grab", position: "relative", background: "#f3f4f6", boxShadow: "0 0 0 3px #0AADA1, 0 0 0 6px rgba(10,173,161,0.15)" }}
                            onPointerDown={(e) => {
                                e.currentTarget.setPointerCapture(e.pointerId);
                                cropDragRef.current = { startX: e.clientX, startY: e.clientY, startPos: { ...cropPos } };
                            }}
                            onPointerMove={(e) => {
                                if (!cropDragRef.current) return;
                                setCropPos({
                                    x: cropDragRef.current.startPos.x + (e.clientX - cropDragRef.current.startX),
                                    y: cropDragRef.current.startPos.y + (e.clientY - cropDragRef.current.startY),
                                });
                            }}
                            onPointerUp={() => { cropDragRef.current = null; }}
                            onWheel={(e) => {
                                e.preventDefault();
                                setCropZoom(z => Math.min(4, Math.max(0.1, z - e.deltaY * 0.003)));
                            }}
                        >
                            <img
                                ref={cropImgRef}
                                src={cropSrc}
                                alt="crop"
                                style={{ position: "absolute", top: "50%", left: "50%", transform: `translate(-50%, -50%) translate(${cropPos.x}px, ${cropPos.y}px) scale(${cropZoom})`, transformOrigin: "center", maxWidth: "none", userSelect: "none", pointerEvents: "none" }}
                                draggable={false}
                            />
                        </div>

                        {/* Zoom slider */}
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "0 12px 20px" }}>
                            <span style={{ fontSize: "11px", color: "#9ca3af" }}>–</span>
                            <input type="range" min="0.1" max="4" step="0.02" value={cropZoom}
                                onChange={e => setCropZoom(Number(e.target.value))}
                                style={{ flex: 1, accentColor: "#0AADA1" }}
                            />
                            <span style={{ fontSize: "13px", color: "#9ca3af" }}>+</span>
                        </div>

                        <div style={{ display: "flex", gap: "8px" }}>
                            <button onClick={() => setCropSrc(null)} style={{ flex: 1, padding: "12px", borderRadius: "12px", border: "1px solid #e5e7eb", background: "#f9fafb", fontSize: "14px", fontWeight: "600", cursor: "pointer", color: "#374151" }}>Cancel</button>
                            <button onClick={confirmCrop} style={{ flex: 1, padding: "12px", borderRadius: "12px", border: "none", background: "linear-gradient(135deg,#0AADA1,#0f8f85)", color: "#fff", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}>Crop & Use</button>
                        </div>
                    </div>
                </div>
            )}

            {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}

            {showVideoModal && promoVideoUrl && (
                <VideoModal
                    url={promoVideoUrl}
                    type={promoVideoType || "youtube"}
                    onClose={() => setShowVideoModal(false)}
                />
            )}
        </div>
    );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function VideoModal({ url, type, onClose }) {
    const ytId = type === "youtube" ? getYoutubeId(url) : null;
    return (
        <div
            style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.92)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
            onClick={onClose}
        >
            <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
            <div style={{ width: "100%", maxWidth: "600px" }} onClick={e => e.stopPropagation()}>
                {ytId ? (
                    <div style={{ position: "relative", paddingBottom: "56.25%", borderRadius: "16px", overflow: "hidden", background: "#000" }}>
                        <iframe src={`https://www.youtube.com/embed/${ytId}?autoplay=1`} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }} allow="autoplay; encrypted-media; fullscreen" allowFullScreen title="Promo Video" />
                    </div>
                ) : (
                    <video src={url} controls autoPlay style={{ width: "100%", borderRadius: "16px", maxHeight: "80vh", background: "#000" }} />
                )}
            </div>
        </div>
    );
}

function getYoutubeId(url) {
    if (!url) return null;
    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
}

function Field({ label, value, onChange, placeholder, type = "text", required, span = 1 }) {
    return (
        <div style={{ gridColumn: `span ${span}` }}>
            <label style={s.label}>{label}</label>
            <input style={s.input} type={type} placeholder={placeholder}
                value={value} onChange={(e) => onChange(e.target.value)} required={required} />
        </div>
    );
}

function ThemeStrip({ selected, onSelect, isPremium, onUpgrade }) {
    const navigate = useNavigate();

    const visibleThemes = (() => {
        const selectedTheme = THEMES.find(t => t.id === selected);
        const others = THEMES.filter(t => t.id !== selected);
        if (selectedTheme) return [selectedTheme, others[0]].filter(Boolean);
        return THEMES.slice(0, 2);
    })();

    const handleThemeClick = (t) => {
        const isFree = FREE_THEMES.includes(t.id);
        if (!isFree && !isPremium) { onUpgrade(); return; }
        onSelect(t.id);
    };

    return (
        <div style={{ marginBottom: "12px" }}>
            <div style={{ fontSize: "10px", fontWeight: "700", color: "rgba(255,255,255,0.4)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: "8px" }}>Card Theme</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
                {visibleThemes.map(t => {
                    const isFree = FREE_THEMES.includes(t.id);
                    const isSelected = selected === t.id;
                    const locked = !isFree && !isPremium;
                    return (
                        <div key={t.id} onClick={() => handleThemeClick(t)} style={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: "5px", opacity: locked ? 0.7 : 1 }}>
                            <div style={{ width: "100%", height: "48px", borderRadius: "8px", background: t.bannerBg || t.headerBg || t.topBand || t.accentBar || t.cardBg, border: isSelected ? "2px solid #0AADA1" : "2px solid transparent", boxShadow: isSelected ? "0 0 0 2px rgba(10,173,161,0.3)" : "none", position: "relative", overflow: "hidden", transition: "border 0.15s" }}>
                                <div style={{ position: "absolute", bottom: "5px", left: "6px", width: "20px", height: "20px", borderRadius: "50%", background: t.avatarBg || t.avatarColor || "#fff", border: `1.5px solid ${t.avatarBorder || t.accent || t.accentColor || "#fff"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "6px", fontWeight: "800", color: t.avatarColor || "#fff" }}>AM</div>
                                {locked && <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px" }}>🔒</div>}
                                {!isFree && !locked && <div style={{ position: "absolute", top: "4px", right: "5px", fontSize: "8px", color: "#f59e0b" }}>★</div>}
                            </div>
                            <div style={{ fontSize: "9px", fontWeight: "600", color: isSelected ? "#0AADA1" : "rgba(255,255,255,0.4)", textAlign: "center", lineHeight: 1.2, width: "100%" }}>{t.name.split(" ")[0]}</div>
                        </div>
                    );
                })}
                <div
                    onClick={() => navigate("/themes", { state: { targetPath: "/create", confirmContext: "create", preselectedTheme: selected } })}
                    style={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}
                >
                    <div style={{ width: "100%", height: "48px", borderRadius: "8px", background: "rgba(10,173,161,0.08)", border: "2px dashed rgba(10,173,161,0.4)", display: "flex", alignItems: "center", justifyContent: "center", transition: "border 0.15s" }}>
                        <span style={{ fontSize: "16px", color: "#0AADA1", lineHeight: 1 }}>+</span>
                    </div>
                    <div style={{ fontSize: "9px", fontWeight: "600", color: "rgba(10,173,161,0.8)", textAlign: "center", lineHeight: 1.2, width: "100%" }}>200+ more</div>
                </div>
            </div>
        </div>
    );
}

function ReviewRow({ icon, label, value }) {
    return (
        <div style={s.reviewRow}>
            <span style={s.reviewIcon}>{icon}</span>
            <div>
                <p style={s.reviewLabel}>{label}</p>
                <p style={s.reviewValue}>{value}</p>
            </div>
        </div>
    );
}

function FormTabIcon({ active }) {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke={active ? "#6366f1" : "#9ca3af"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
    );
}

function PreviewTabIcon({ active }) {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke={active ? "#0AADA1" : "#9ca3af"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
    );
}

function GoogleIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
    );
}

function CameraIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
        </svg>
    );
}

// ─── Styles ────────────────────────────────────────────────────────────────────
const s = {
    // ── Desktop layout ──────────────────────────────────────────────────────────
    page: {
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: "1fr 420px",
        fontFamily: "'Segoe UI', Arial, sans-serif",
    },
    formPanel: {
        background: "#fff",
        display: "flex", flexDirection: "column",
        borderRight: "1px solid #f3f4f6",
        minHeight: "100vh",
    },
    previewPanel: {
        background: "linear-gradient(160deg, #0f0c29 0%, #1a1a2e 50%, #0f3460 100%)",
        padding: "2rem 1.5rem",
        display: "flex", flexDirection: "column", alignItems: "center",
        position: "sticky", top: 0, height: "100vh", overflowY: "auto",
    },
    previewInner: { width: "100%", maxWidth: "420px" },

    // ── Mobile layout ───────────────────────────────────────────────────────────
    mobilePage: {
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        fontFamily: "'Segoe UI', Arial, sans-serif",
        position: "relative",
    },
    mobilePanel: {
        flex: 1,
        background: "#fff",
        minHeight: "100vh",
        overflowY: "auto",
    },
    mobilePreviewPanel: {
        flex: 1,
        background: "linear-gradient(160deg, #0f0c29 0%, #1a1a2e 50%, #0f3460 100%)",
        minHeight: "100vh",
        padding: "1.5rem 1rem",
    },
    mobilePreviewInner: {
        width: "100%",
        maxWidth: "420px",
    },

    // ── Mobile tab bar ──────────────────────────────────────────────────────────
    mobileTabBar: {
        position: "fixed",
        bottom: 0, left: 0, right: 0,
        height: "64px",
        background: "#fff",
        borderTop: "1px solid #e5e7eb",
        display: "flex",
        alignItems: "stretch",
        zIndex: 100,
        boxShadow: "0 -4px 20px rgba(0,0,0,0.08)",
    },
    mobileTabBtn: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "3px",
        background: "none",
        border: "none",
        cursor: "pointer",
        fontFamily: "'Segoe UI', Arial, sans-serif",
        paddingTop: "2px",
        transition: "color 0.2s",
        position: "relative",
    },
    mobileTabLabel: {
        fontSize: "11px",
        fontWeight: "700",
        letterSpacing: "0.3px",
    },
    mobileTabPill: {
        fontSize: "9px",
        fontWeight: "700",
        padding: "1px 6px",
        borderRadius: "20px",
        letterSpacing: "0.2px",
    },
    mobileTabDivider: {
        width: "1px",
        background: "#e5e7eb",
        margin: "12px 0",
    },
    liveDot: {
        position: "absolute",
        top: "8px",
        right: "calc(50% - 18px)",
        width: "7px",
        height: "7px",
        borderRadius: "50%",
        background: "#22c55e",
        border: "1.5px solid #fff",
    },

    // ── Mobile nudge banner ─────────────────────────────────────────────────────
    nudgeBanner: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        background: "#eff6ff",
        border: "1px solid #bfdbfe",
        borderRadius: "12px",
        padding: "10px 14px",
        marginBottom: "1rem",
    },
    nudgeIcon: { fontSize: "16px", flexShrink: 0 },
    nudgeText: { fontSize: "12px", color: "#1e40af", flex: 1, lineHeight: 1.4 },
    nudgeClose: {
        background: "none", border: "none", fontSize: "14px",
        color: "#93c5fd", cursor: "pointer", padding: 0, flexShrink: 0,
    },

    // ── Shared top bar / steps ──────────────────────────────────────────────────
    topBar: {
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "1.25rem 2rem", borderBottom: "1px solid #f3f4f6",
    },
    backBtn: {
        background: "none", border: "none", color: "#6b7280",
        fontSize: "0.9rem", fontWeight: "600", cursor: "pointer", padding: 0,
    },
    topLogo: {
        fontSize: "1rem", fontWeight: "800", color: "#1a1a2e",
        display: "flex", alignItems: "center", gap: "5px",
    },
    logoDot: { color: "#6366f1", fontSize: "0.75rem" },

    stepBar: {
        display: "flex", alignItems: "flex-start",
        padding: "1.5rem 2rem",
        borderBottom: "1px solid #f3f4f6",
        gap: 0,
    },
    stepItem: {
        display: "flex", flexDirection: "column", alignItems: "center",
        position: "relative", flex: 1,
    },
    stepCircle: {
        width: "32px", height: "32px", borderRadius: "50%",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "0.8rem", fontWeight: "800",
        transition: "all 0.3s", zIndex: 1,
    },
    stepLabel: {
        fontSize: "0.7rem", marginTop: "6px", textAlign: "center",
        transition: "all 0.3s", whiteSpace: "nowrap",
    },
    stepLine: {
        position: "absolute", top: "16px", left: "calc(50% + 16px)",
        width: "calc(100% - 32px)", height: "2px",
        transition: "background 0.3s",
    },

    // ── Form body ───────────────────────────────────────────────────────────────
    formBody: {
        flex: 1, padding: "2rem", overflowY: "auto",
    },
    stepContent: { maxWidth: "560px", margin: "0 auto" },
    stepHeading: { fontSize: "1.5rem", fontWeight: "900", color: "#1a1a2e", margin: "0 0 0.4rem" },
    stepSub: { fontSize: "0.9rem", color: "#6b7280", margin: "0 0 2rem", lineHeight: 1.6 },

    // ── Auto-fill ───────────────────────────────────────────────────────────────
    autoFillCard: {
        position: "relative", borderRadius: "16px", padding: "18px 20px",
        border: "1.5px solid rgba(10,173,161,0.3)", marginBottom: "1.5rem",
        transition: "all 0.25s cubic-bezier(0.22,1,0.36,1)", overflow: "hidden",
    },
    autoFillTopBar: {
        position: "absolute", top: 0, left: "10%", right: "10%", height: "2px",
        background: "linear-gradient(90deg,transparent,#0AADA1,#6366f1,transparent)",
        borderRadius: "2px",
    },
    autoFillIcon: {
        width: "48px", height: "48px", borderRadius: "14px", flexShrink: 0,
        background: "linear-gradient(135deg,rgba(10,173,161,0.15),rgba(99,102,241,0.1))",
        border: "1px solid rgba(10,173,161,0.2)",
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px",
    },
    autoFillTitle: { fontSize: "14px", fontWeight: "800", color: "#1a1a2e", marginBottom: "3px" },
    autoFillSub: { fontSize: "12px", color: "#6b7280", lineHeight: 1.5 },
    autoFillBtn: {
        padding: "9px 18px", borderRadius: "10px", border: "none", flexShrink: 0,
        background: "linear-gradient(135deg,#0C9189,#0AADA1)", color: "#fff",
        fontSize: "12px", fontWeight: "700", cursor: "pointer", whiteSpace: "nowrap",
        boxShadow: "0 4px 12px rgba(10,173,161,0.3)",
        fontFamily: "'Segoe UI', Arial, sans-serif",
    },
    autoFillScanLabel: { fontSize: "13px", fontWeight: "700", color: "#1a1a2e", marginBottom: "12px" },
    autoFillScanBar: {
        height: "4px", borderRadius: "2px", background: "rgba(10,173,161,0.15)",
        overflow: "hidden", margin: "0 auto 8px", maxWidth: "300px",
    },
    autoFillScanFill: {
        height: "100%", borderRadius: "2px", width: "40%",
        background: "linear-gradient(90deg,#0AADA1,#6366f1)",
        animation: "scan-slide 1.2s ease-in-out infinite alternate",
    },
    autoFillScanSub: { fontSize: "11px", color: "#9ca3af" },
    autoFillSuccessIcon: {
        width: "40px", height: "40px", borderRadius: "50%", flexShrink: 0,
        background: "#22c55e", color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "18px", fontWeight: "800", boxShadow: "0 4px 12px rgba(34,197,94,0.35)",
    },
    autoFillSuccessTitle: { fontSize: "14px", fontWeight: "800", color: "#15803d", marginBottom: "2px" },
    autoFillSuccessSub: { fontSize: "12px", color: "#6b7280" },
    autoFillReparse: {
        padding: "7px 14px", borderRadius: "8px", flexShrink: 0,
        border: "1px solid rgba(34,197,94,0.3)", background: "transparent",
        fontSize: "11px", fontWeight: "700", color: "#15803d", cursor: "pointer",
        fontFamily: "'Segoe UI', Arial, sans-serif",
    },

    // ── Photo / banner ──────────────────────────────────────────────────────────
    photoArea: {
        display: "flex", alignItems: "center", gap: "1.5rem",
        background: "#f8f9ff", borderRadius: "16px",
        padding: "1.25rem 1.5rem", marginBottom: "1.5rem",
        border: "1.5px solid #e5e7eb",
    },
    avatarWrap: { position: "relative", width: "72px", height: "72px", flexShrink: 0 },
    avatarImg: { width: "72px", height: "72px", borderRadius: "50%", objectFit: "cover", border: "3px solid #e5e7eb" },
    avatarInitials: {
        width: "72px", height: "72px", borderRadius: "50%",
        background: "linear-gradient(135deg, #1a1a2e, #0f3460)",
        color: "#fff", display: "flex", alignItems: "center",
        justifyContent: "center", fontSize: "1.4rem", fontWeight: "700",
    },
    cameraBtn: {
        position: "absolute", bottom: "0px", right: "0px",
        width: "26px", height: "26px", borderRadius: "50%",
        background: "#6366f1", display: "flex", alignItems: "center",
        justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 6px rgba(99,102,241,0.4)",
    },
    photoTitle: { fontWeight: "700", fontSize: "0.9rem", color: "#1a1a2e", margin: "0 0 0.2rem" },
    photoHint: { fontSize: "0.78rem", color: "#9ca3af", margin: "0 0 0.6rem" },
    photoUploadBtn: {
        display: "inline-block", padding: "0.4rem 1rem",
        background: "#f3f4f6", border: "1.5px solid #e5e7eb",
        borderRadius: "8px", fontSize: "0.82rem", fontWeight: "600",
        color: "#374151", cursor: "pointer",
    },
    bannerArea: {
        display: "flex", alignItems: "flex-start", gap: "1rem",
        background: "#f9fafb", borderRadius: "14px",
        padding: "1rem", border: "1.5px dashed #e5e7eb", marginBottom: "1rem",
    },
    bannerPreviewBox: {
        width: "120px", height: "52px", borderRadius: "8px",
        background: "#e5e7eb", overflow: "hidden", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
    },
    bannerImg: { width: "100%", height: "100%", objectFit: "cover" },
    bannerPlaceholder: { fontSize: "0.7rem", color: "#9ca3af", textAlign: "center" },

    // ── Fields ──────────────────────────────────────────────────────────────────
    fieldGrid: {
        display: "grid", gridTemplateColumns: "1fr 1fr",
        gap: "1rem", marginBottom: "1rem",
    },
    field: { display: "flex", flexDirection: "column", gap: "0.35rem", marginBottom: "1rem" },
    label: { fontSize: "0.8rem", fontWeight: "700", color: "#374151", marginBottom: "0.35rem", display: "block" },
    input: {
        padding: "0.75rem 1rem", border: "1.5px solid #e5e7eb",
        borderRadius: "10px", fontSize: "0.92rem", outline: "none",
        fontFamily: "inherit", width: "100%", boxSizing: "border-box",
        transition: "border-color 0.2s",
    },

    // ── Resume ──────────────────────────────────────────────────────────────────
    resumeUploadArea: {
        display: "flex", alignItems: "center", gap: "1rem",
        background: "#f8f9ff", borderRadius: "14px",
        padding: "1rem 1.25rem", border: "1.5px dashed #d1d5db",
        marginTop: "1rem",
    },
    resumeIcon: { fontSize: "1.5rem" },
    resumeTitle: { fontWeight: "700", fontSize: "0.88rem", color: "#1a1a2e", margin: "0 0 0.2rem" },
    resumeHint: { fontSize: "0.78rem", color: "#9ca3af", margin: 0 },
    resumeBtn: {
        padding: "0.5rem 1rem", background: "#6366f1",
        color: "#fff", border: "none", borderRadius: "8px",
        fontSize: "0.82rem", fontWeight: "700", cursor: "pointer",
        flexShrink: 0, whiteSpace: "nowrap",
    },

    // ── Social links ────────────────────────────────────────────────────────────
    linkRow: {
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0.75rem 0",
    },
    linkLeft: { display: "flex", alignItems: "center", gap: "0.75rem" },
    linkIcon: {
        width: "38px", height: "38px", borderRadius: "10px",
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem",
    },
    socialBadge: {
        width: "38px", height: "38px", borderRadius: "10px",
        display: "flex", alignItems: "center", justifyContent: "center",
    },
    linkLabel: { fontWeight: "700", fontSize: "0.9rem", color: "#1a1a2e", margin: 0 },
    linkHint: { fontSize: "0.75rem", color: "#9ca3af", margin: 0 },
    toggle: { cursor: "pointer", position: "relative" },
    toggleTrack: {
        width: "44px", height: "24px", borderRadius: "50px",
        position: "relative", transition: "background 0.25s",
    },
    toggleThumb: {
        width: "20px", height: "20px", borderRadius: "50%",
        background: "#fff", position: "absolute", top: "2px",
        transition: "left 0.25s", boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
    },
    divider: { height: "1px", background: "#f3f4f6", margin: "0.5rem 0" },

    // ── Review ──────────────────────────────────────────────────────────────────
    reviewGrid: { display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "2rem" },
    reviewRow: {
        display: "flex", alignItems: "center", gap: "1rem",
        background: "#f8f9ff", borderRadius: "12px",
        padding: "0.75rem 1rem", border: "1px solid #e5e7eb",
    },
    reviewIcon: { fontSize: "1.2rem", width: "28px", textAlign: "center" },
    reviewLabel: { fontSize: "0.72rem", fontWeight: "600", color: "#9ca3af", margin: "0 0 0.1rem", textTransform: "uppercase", letterSpacing: "0.5px" },
    reviewValue: { fontSize: "0.9rem", fontWeight: "600", color: "#1a1a2e", margin: 0 },

    // ── Navigation ──────────────────────────────────────────────────────────────
    navRow: {
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginTop: "2rem", paddingTop: "1.5rem", borderTop: "1px solid #f3f4f6",
    },
    backStepBtn: {
        background: "none", border: "1.5px solid #e5e7eb",
        borderRadius: "10px", padding: "0.75rem 1.5rem",
        fontSize: "0.9rem", fontWeight: "600", color: "#6b7280", cursor: "pointer",
    },
    nextBtn: {
        marginLeft: "auto",
        padding: "0.75rem 2rem",
        background: "linear-gradient(135deg, #6366f1, #4f46e5)",
        color: "#fff", border: "none", borderRadius: "10px",
        fontSize: "0.95rem", fontWeight: "700", cursor: "pointer",
        boxShadow: "0 4px 16px rgba(99,102,241,0.3)",
    },
    createBtn: {
        width: "100%", padding: "1rem",
        background: "linear-gradient(135deg, #6366f1, #4f46e5)",
        color: "#fff", border: "none", borderRadius: "12px",
        fontSize: "1.05rem", fontWeight: "800", cursor: "pointer",
        boxShadow: "0 8px 24px rgba(99,102,241,0.35)",
        marginBottom: "0.75rem",
    },
    reviewNote: { textAlign: "center", fontSize: "0.8rem", color: "#9ca3af", margin: 0 },
    error: { color: "#ef4444", fontSize: "0.875rem", margin: "0 0 1rem", textAlign: "center" },

    // ── Auth ────────────────────────────────────────────────────────────────────
    authPage: { minHeight: "100vh", background: "linear-gradient(135deg, #0f0c29 0%, #1a1a2e 50%, #0f3460 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", fontFamily: "'Segoe UI', Arial, sans-serif" },
    authBox: { background: "#fff", borderRadius: "24px", padding: "3rem 2.5rem", width: "100%", maxWidth: "420px", textAlign: "center", boxShadow: "0 40px 80px rgba(0,0,0,0.5)" },
    authLogo: { fontSize: "1.1rem", fontWeight: "800", color: "#1a1a2e", display: "inline-flex", alignItems: "center", gap: "6px", marginBottom: "1.5rem" },
    authTitle: { fontSize: "1.6rem", fontWeight: "900", color: "#1a1a2e", margin: "0 0 0.75rem" },
    authSub: { fontSize: "0.9rem", color: "#6b7280", margin: "0 0 2rem", lineHeight: 1.6 },
    googleBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem", width: "100%", padding: "0.9rem", background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: "12px", fontSize: "0.95rem", fontWeight: "700", color: "#1a1a2e", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", marginBottom: "1rem" },
    authNote: { fontSize: "0.78rem", color: "#9ca3af", margin: 0 },

    // ── Slug ────────────────────────────────────────────────────────────────────
    slugWrap: { display: "flex", alignItems: "center", border: "1.5px solid #e5e7eb", borderRadius: "10px", overflow: "hidden" },
    slugBase: { background: "#f3f4f6", padding: "0.75rem 0.75rem", fontSize: "0.82rem", color: "#6b7280", fontWeight: "600", whiteSpace: "nowrap", borderRight: "1.5px solid #e5e7eb" },
    slugInput: { flex: 1, padding: "0.75rem 0.75rem", border: "none", outline: "none", fontSize: "0.92rem", fontFamily: "inherit" },
    slugStatus: { marginTop: "0.4rem", fontSize: "0.78rem", lineHeight: 1.5 },
    slugAvailable: { color: "#16a34a" },
    slugTaken: { color: "#dc2626" },
    slugChecking: { color: "#9ca3af" },
};




// import { useState, useEffect, useRef } from "react";
// import { useLocation, useNavigate } from "react-router-dom";
// import { collection, addDoc, getDocs, query, where } from "firebase/firestore";
// import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
// import db, { storage } from "../Firebase";
// import { useAuth } from "../AuthContext";
// import { THEMES, FREE_THEMES, CardRenderer, CARD_FONTS } from "../CardThemes";

// const SOCIAL_PLATFORMS = [
//     { key: "linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/in/yourname", color: "#0A66C2", icon: "in" },
//     { key: "twitter", label: "Twitter / X", placeholder: "https://twitter.com/yourhandle", color: "#000000", icon: "𝕏" },
//     { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/yourname", color: "#E1306C", icon: "IG" },
//     { key: "facebook", label: "Facebook", placeholder: "https://facebook.com/yourname", color: "#1877F2", icon: "f" },
//     { key: "github", label: "GitHub", placeholder: "https://github.com/yourname", color: "#333333", icon: "GH" },
//     { key: "whatsapp", label: "WhatsApp", placeholder: "+91 98765 43210", color: "#25D366", icon: "W" },
//     { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/@channel", color: "#FF0000", icon: "▶" },
// ];

// const initialSocials = Object.fromEntries(
//     SOCIAL_PLATFORMS.map((p) => [p.key, { enabled: false, url: "" }])
// );

// const STEPS = ["Basic Info", "Professional", "Links & Social", "Review"];
// const DRAFT_KEY = "mpc_create_draft";

// export default function Home() {
//     const routerLocation = useLocation();

//     // Restore draft from localStorage on first mount
//     const savedDraft = (() => {
//         try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; }
//     })();

//     const [step, setStep] = useState(savedDraft?.step ?? 0);
//     const [name, setName] = useState(savedDraft?.name ?? "");
//     const [phone, setPhone] = useState(savedDraft?.phone ?? "");
//     const [email, setEmail] = useState(savedDraft?.email ?? "");
//     const [location, setLocation] = useState(savedDraft?.location ?? "");
//     const [designation, setDesignation] = useState(savedDraft?.designation ?? "");
//     const [company, setCompany] = useState(savedDraft?.company ?? "");
//     const [bio, setBio] = useState(savedDraft?.bio ?? "");
//     const [websiteEnabled, setWebsiteEnabled] = useState(savedDraft?.websiteEnabled ?? false);
//     const [website, setWebsite] = useState(savedDraft?.website ?? "");
//     const [socials, setSocials] = useState(savedDraft?.socials ?? initialSocials);
//     const [photoFile, setPhotoFile] = useState(null);
//     const [photoPreview, setPhotoPreview] = useState(null);
//     const [bannerFile, setBannerFile] = useState(null);
//     const [bannerPreview, setBannerPreview] = useState(null);
//     const [resumeFile, setResumeFile] = useState(null);
//     const [resumeName, setResumeName] = useState(savedDraft?.resumeName ?? "");
//     const [slug, setSlug] = useState(savedDraft?.slug ?? "");
//     const [slugStatus, setSlugStatus] = useState("");
//     const [slugManual, setSlugManual] = useState(savedDraft?.slugManual ?? false);
//     const [theme, setTheme] = useState(routerLocation.state?.preselectedTheme ?? savedDraft?.theme ?? "dark");
//     const [loading, setLoading] = useState(false);
//     const [error, setError] = useState("");
//     const [parsing, setParsing] = useState(false);
//     const [parseSuccess, setParseSuccess] = useState(false);
//     const [parsedCount, setParsedCount] = useState(0);
//     const [dragOver, setDragOver] = useState(false);
//     const parseInputRef = useRef(null);
//     const slugTimer = useRef(null);
//     const navigate = useNavigate();
//     const { user, signInWithGoogle } = useAuth();

//     const SERVER = process.env.NODE_ENV === "production"
//         ? "https://myprofilecorner.com/api"
//         : "http://localhost:3001";

//     const handleAutoFill = async (file) => {
//         if (!file || file.type !== "application/pdf") return;
//         setParsing(true);
//         setParseSuccess(false);
//         try {
//             const formData = new FormData();
//             formData.append("resume", file);
//             const res = await fetch(`${SERVER}/parse-resume`, { method: "POST", body: formData });
//             if (!res.ok) throw new Error("Parse failed");
//             const data = await res.json();

//             const SOCIAL_KEYS = ["linkedin", "github", "twitter", "instagram", "whatsapp"];
//             let count = 0;
//             const fields = [
//                 [data.name,        setName],
//                 [data.phone,       setPhone],
//                 [data.email,       setEmail],
//                 [data.location,    setLocation],
//                 [data.designation, setDesignation],
//                 [data.company,     setCompany],
//                 [data.bio,         setBio],
//                 [data.website,     (v) => { if (v) { setWebsiteEnabled(true); setWebsite(v); } }],
//             ];
//             // Cascade fill with small delay between each field for a nice effect
//             for (let i = 0; i < fields.length; i++) {
//                 const [val, setter] = fields[i];
//                 if (val) { await delay(80); setter(val); count++; }
//             }
//             // Social links
//             for (const key of SOCIAL_KEYS) {
//                 if (data[key]) {
//                     await delay(80);
//                     setSocials(prev => ({ ...prev, [key]: { enabled: true, url: data[key] } }));
//                     count++;
//                 }
//             }
//             setParsedCount(count);
//             setParseSuccess(true);
//         } catch {
//             alert("Couldn't read the resume. Please try a different PDF.");
//         } finally {
//             setParsing(false);
//         }
//     };

//     const delay = (ms) => new Promise(r => setTimeout(r, ms));

//     const initials = name.trim()
//         ? name.trim().split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
//         : "?";

//     const enabledSocials = SOCIAL_PLATFORMS.filter(p => socials[p.key].enabled);

//     // Save draft to localStorage whenever form data changes
//     useEffect(() => {
//         try {
//             localStorage.setItem(DRAFT_KEY, JSON.stringify({
//                 step, name, phone, email, location, designation, company, bio,
//                 websiteEnabled, website, socials, resumeName, slug, slugManual, theme,
//             }));
//         } catch { /* quota exceeded — ignore */ }
//     }, [step, name, phone, email, location, designation, company, bio,
//         websiteEnabled, website, socials, resumeName, slug, slugManual, theme]);

//     useEffect(() => {
//         if (routerLocation.state?.preselectedTheme) {
//             setTheme(routerLocation.state.preselectedTheme);
//         }
//     }, [routerLocation.state]);

//     // Re-check slug on mount if restored from draft (slugStatus resets to "")
//     useEffect(() => {
//         if (slug) checkSlug(slug);
//     }, []); // eslint-disable-line react-hooks/exhaustive-deps

//     // Auto-generate slug from name unless user typed manually
//     useEffect(() => {
//         if (slugManual) return;
//         const auto = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
//         setSlug(auto);
//         if (auto) checkSlug(auto);
//     }, [name]);

//     const checkSlug = async (value) => {
//         if (!value) { setSlugStatus(""); return; }
//         setSlugStatus("checking");
//         clearTimeout(slugTimer.current);
//         slugTimer.current = setTimeout(async () => {
//             const snap = await getDocs(query(collection(db, "cards"), where("slug", "==", value)));
//             setSlugStatus(snap.empty ? "available" : "taken");
//         }, 500);
//     };

//     const handleSlugChange = (raw) => {
//         const clean = raw.toLowerCase().replace(/[^a-z0-9-]/g, "").replace(/--+/g, "-");
//         setSlug(clean);
//         setSlugManual(true);
//         checkSlug(clean);
//     };

//     const handlePhotoChange = (e) => {
//         const file = e.target.files[0];
//         if (!file) return;
//         setPhotoFile(file);
//         setPhotoPreview(URL.createObjectURL(file));
//     };

//     const handleBannerChange = (e) => {
//         const file = e.target.files[0];
//         if (!file) return;
//         setBannerFile(file);
//         setBannerPreview(URL.createObjectURL(file));
//     };

//     const handleResumeChange = (e) => {
//         const file = e.target.files[0];
//         if (!file) return;
//         setResumeFile(file);
//         setResumeName(file.name);
//     };

//     const toggleSocial = (key) =>
//         setSocials(prev => ({ ...prev, [key]: { ...prev[key], enabled: !prev[key].enabled } }));

//     const updateSocialUrl = (key, url) =>
//         setSocials(prev => ({ ...prev, [key]: { ...prev[key], url } }));

//     const canNext = () => {
//         if (step === 0) return name.trim() && phone.trim() && email.trim() && slug && slugStatus === "available";
//         return true;
//     };

//     const handleSubmit = async () => {
//         if (slugStatus !== "available") { setError("Please choose an available URL handle."); return; }
//         setLoading(true);
//         setError("");
//         try {
//             let photoUrl = "";
//             if (photoFile) {
//                 const photoRef = ref(storage, `profile-photos/${slug}_${Date.now()}`);
//                 await uploadBytes(photoRef, photoFile);
//                 photoUrl = await getDownloadURL(photoRef);
//             }
//             let bannerUrl = "";
//             if (bannerFile) {
//                 const bannerRef = ref(storage, `banners/${slug}_${Date.now()}`);
//                 await uploadBytes(bannerRef, bannerFile);
//                 bannerUrl = await getDownloadURL(bannerRef);
//             }
//             let resumeUrl = "";
//             if (resumeFile) {
//                 const resumeRef = ref(storage, `resumes/${slug}_${Date.now()}_${resumeFile.name}`);
//                 await uploadBytes(resumeRef, resumeFile);
//                 resumeUrl = await getDownloadURL(resumeRef);
//             }
//             const socialsToSave = {};
//             SOCIAL_PLATFORMS.forEach(({ key }) => {
//                 if (socials[key].enabled && socials[key].url.trim())
//                     socialsToSave[key] = socials[key].url.trim();
//             });
//             await addDoc(collection(db, "cards"), {
//                 slug, name: name.trim(), phone: phone.trim(), email: email.trim(),
//                 location: location.trim(), designation: designation.trim(),
//                 company: company.trim(), bio: bio.trim(),
//                 website: websiteEnabled ? website.trim() : "",
//                 photoUrl, bannerUrl, resumeUrl, socials: socialsToSave,
//                 theme,
//                 userId: user.uid,
//                 createdAt: new Date(),
//             });
//             localStorage.removeItem(DRAFT_KEY);
//             navigate(`/card/${slug}`);
//         } catch (err) {
//             console.error("Full error:", err);
//             console.error("Error code:", err.code);
//             console.error("Error message:", err.message);
//             setError(`Error: ${err.code || err.message}`);
//             setLoading(false);
//         }
//     };

//     // Check if logged-in user already has a card
//     useEffect(() => {
//         if (!user) return;
//         const checkExisting = async () => {
//             const snap = await getDocs(query(collection(db, "cards"), where("userId", "==", user.uid)));
//             if (!snap.empty) {
//                 const existing = snap.docs[0].data();
//                 navigate(`/card/${existing.slug}`, { replace: true });
//             }
//         };
//         checkExisting();
//     }, [user]);

//     // Loading auth state
//     if (user === undefined) {
//         return <div style={s.authPage}><p style={{ color: "#fff" }}>Loading...</p></div>;
//     }

//     // Not logged in — show sign in screen
//     if (!user) {
//         return (
//             <div style={s.authPage}>
//                 <div style={s.authBox}>
//                     <div style={s.authLogo}><span style={s.logoDot}>●</span> MyProfileCorner</div>
//                     <h2 style={s.authTitle}>Sign in to create your card</h2>
//                     <p style={s.authSub}>We use Google Sign-In to keep your card secure. Only you can edit it.</p>
//                     <button onClick={signInWithGoogle} style={s.googleBtn}>
//                         <GoogleIcon /> Continue with Google
//                     </button>
//                     <p style={s.authNote}>Free forever · No spam · Cancel anytime</p>
//                 </div>
//             </div>
//         );
//     }

//     return (
//         <div style={s.page}>
//             <style>{`@keyframes scan-slide { from { margin-left:0%; } to { margin-left:60%; } }`}</style>
//             {/* Left — Form Panel */}
//             <div style={s.formPanel}>

//                 {/* Top bar */}
//                 <div style={s.topBar}>
//                     <button onClick={() => navigate("/")} style={s.backBtn}>← Home</button>
//                     <span style={s.topLogo}><span style={s.logoDot}>●</span> MyProfileCorner</span>
//                 </div>

//                 {/* Progress Steps */}
//                 <div style={s.stepBar}>
//                     {STEPS.map((label, i) => (
//                         <div key={i} style={s.stepItem}>
//                             <div style={{
//                                 ...s.stepCircle,
//                                 background: i < step ? "#6366f1" : i === step ? "#6366f1" : "#e5e7eb",
//                                 color: i <= step ? "#fff" : "#9ca3af",
//                                 boxShadow: i === step ? "0 0 0 4px rgba(99,102,241,0.2)" : "none",
//                             }}>
//                                 {i < step ? "✓" : i + 1}
//                             </div>
//                             <span style={{
//                                 ...s.stepLabel,
//                                 color: i === step ? "#6366f1" : i < step ? "#1a1a2e" : "#9ca3af",
//                                 fontWeight: i === step ? "700" : "500",
//                             }}>{label}</span>
//                             {i < STEPS.length - 1 && (
//                                 <div style={{ ...s.stepLine, background: i < step ? "#6366f1" : "#e5e7eb" }} />
//                             )}
//                         </div>
//                     ))}
//                 </div>

//                 {/* Form Content */}
//                 <div style={s.formBody}>

//                     {/* STEP 0 — Basic Info */}
//                     {step === 0 && (
//                         <div style={s.stepContent}>
//                             <h2 style={s.stepHeading}>Let's start with the basics</h2>
//                             <p style={s.stepSub}>Your core contact information — this is what people see first.</p>

//                             {/* ── AI Resume Auto-fill ── */}
//                             <div
//                                 style={{
//                                     ...s.autoFillCard,
//                                     borderColor: dragOver ? "#0AADA1" : parseSuccess ? "#22c55e" : "rgba(10,173,161,0.3)",
//                                     background: parseSuccess
//                                         ? "linear-gradient(135deg,rgba(34,197,94,0.06),rgba(34,197,94,0.02))"
//                                         : dragOver
//                                         ? "linear-gradient(135deg,rgba(10,173,161,0.1),rgba(99,102,241,0.06))"
//                                         : "linear-gradient(135deg,rgba(10,173,161,0.05),rgba(99,102,241,0.03))",
//                                     transform: dragOver ? "scale(1.01)" : "scale(1)",
//                                 }}
//                                 onDragOver={e => { e.preventDefault(); setDragOver(true); }}
//                                 onDragLeave={() => setDragOver(false)}
//                                 onDrop={e => { e.preventDefault(); setDragOver(false); handleAutoFill(e.dataTransfer.files[0]); }}
//                             >
//                                 {/* Shimmer top border */}
//                                 <div style={s.autoFillTopBar} />

//                                 {parseSuccess ? (
//                                     /* ── Success state ── */
//                                     <div style={{ display:"flex", alignItems:"center", gap:"14px" }}>
//                                         <div style={s.autoFillSuccessIcon}>✓</div>
//                                         <div style={{ flex:1 }}>
//                                             <div style={s.autoFillSuccessTitle}>{parsedCount} fields auto-filled from your resume!</div>
//                                             <div style={s.autoFillSuccessSub}>Review below and adjust anything if needed.</div>
//                                         </div>
//                                         <button style={s.autoFillReparse} onClick={() => parseInputRef.current?.click()}>
//                                             ↺ Re-scan
//                                         </button>
//                                     </div>
//                                 ) : parsing ? (
//                                     /* ── Loading state ── */
//                                     <div style={{ textAlign:"center", padding:"8px 0" }}>
//                                         <div style={s.autoFillScanLabel}>🤖 Reading your resume with AI...</div>
//                                         <div style={s.autoFillScanBar}>
//                                             <div style={s.autoFillScanFill} />
//                                         </div>
//                                         <div style={s.autoFillScanSub}>Extracting name, contact, experience & links</div>
//                                     </div>
//                                 ) : (
//                                     /* ── Default / drag state ── */
//                                     <div style={{ display:"flex", alignItems:"center", gap:"16px" }}>
//                                         <div style={s.autoFillIcon}>{dragOver ? "📂" : "✨"}</div>
//                                         <div style={{ flex:1 }}>
//                                             <div style={s.autoFillTitle}>Auto-fill from Resume</div>
//                                             <div style={s.autoFillSub}>
//                                                 {dragOver ? "Drop your PDF here!" : "Upload your PDF — AI fills everything in seconds"}
//                                             </div>
//                                         </div>
//                                         <button style={s.autoFillBtn} onClick={() => parseInputRef.current?.click()}>
//                                             Upload PDF
//                                         </button>
//                                     </div>
//                                 )}

//                                 <input
//                                     ref={parseInputRef}
//                                     type="file"
//                                     accept="application/pdf"
//                                     style={{ display:"none" }}
//                                     onChange={e => handleAutoFill(e.target.files[0])}
//                                 />
//                             </div>

//                             {/* Photo Upload */}
//                             <div style={s.photoArea}>
//                                 <div style={s.avatarWrap}>
//                                     {photoPreview
//                                         ? <img src={photoPreview} alt="preview" style={s.avatarImg} />
//                                         : <div style={s.avatarInitials}>{initials}</div>
//                                     }
//                                     <label htmlFor="photo-input" style={s.cameraBtn}>
//                                         <CameraIcon />
//                                     </label>
//                                     <input id="photo-input" type="file" accept="image/*"
//                                         onChange={handlePhotoChange} style={{ display: "none" }} />
//                                 </div>
//                                 <div>
//                                     <p style={s.photoTitle}>Profile Photo</p>
//                                     <p style={s.photoHint}>Appears on your card and Google Wallet pass</p>
//                                     <label htmlFor="photo-input" style={s.photoUploadBtn}>Upload Photo</label>
//                                 </div>
//                             </div>

//                             {/* Banner Upload (dark theme only) */}
//                             {theme === "dark" && (
//                                 <div style={s.bannerArea}>
//                                     <div style={s.bannerPreviewBox}>
//                                         {bannerPreview
//                                             ? <img src={bannerPreview} alt="banner" style={s.bannerImg} />
//                                             : <span style={s.bannerPlaceholder}>🖼 Cover Image</span>
//                                         }
//                                     </div>
//                                     <div>
//                                         <p style={s.photoTitle}>Cover / Banner Image</p>
//                                         <p style={s.photoHint}>Wide image shown at top of dark card (16:7 ratio works best)</p>
//                                         <label htmlFor="banner-input" style={s.photoUploadBtn}>Upload Banner</label>
//                                         <input id="banner-input" type="file" accept="image/*"
//                                             onChange={handleBannerChange} style={{ display: "none" }} />
//                                     </div>
//                                 </div>
//                             )}

//                             <div style={s.fieldGrid}>
//                                 <Field label="Full Name *" value={name} onChange={setName} placeholder="Enter Full Name" required span={2} />
//                                 <Field label="Phone Number *" value={phone} onChange={setPhone} placeholder="+91 98********" required />
//                                 <Field label="Email Address *" type="email" value={email} onChange={setEmail} placeholder="yourid@example.com" required />
//                                 <Field label="Location" value={location} onChange={setLocation} placeholder="eg : Gurugram, Haryana" span={2} />
//                             </div>

//                             {/* ── URL Handle ── */}
//                             <div style={s.field}>
//                                 <label style={s.label}>Your Card URL *</label>
//                                 <div style={s.slugWrap}>
//                                     <span style={s.slugBase}>myprofilecorner.com/</span>
//                                     <input
//                                         style={{ ...s.slugInput, borderColor: slugStatus === "available" ? "#22c55e" : slugStatus === "taken" ? "#ef4444" : "#e5e7eb" }}
//                                         value={slug}
//                                         onChange={(e) => handleSlugChange(e.target.value)}
//                                         placeholder="your-name"
//                                     />
//                                 </div>
//                                 <div style={s.slugStatus}>
//                                     {slugStatus === "checking" && <span style={s.slugChecking}>⏳ Checking availability...</span>}
//                                     {slugStatus === "available" && <span style={s.slugAvailable}>✓ Available — your card will be at <b>myprofilecorner.com/VisitingCard/{slug}</b></span>}
//                                     {slugStatus === "taken" && <span style={s.slugTaken}>✗ Already taken — try a different handle</span>}
//                                     {!slugStatus && slug && <span style={s.slugChecking}>Type to check availability</span>}
//                                 </div>
//                             </div>
//                         </div>
//                     )}

//                     {/* STEP 1 — Professional */}
//                     {step === 1 && (
//                         <div style={s.stepContent}>
//                             <h2 style={s.stepHeading}>Your professional profile</h2>
//                             <p style={s.stepSub}>Help people understand who you are and what you do.</p>

//                             <div style={s.fieldGrid}>
//                                 <Field label="Designation / Role" value={designation} onChange={setDesignation} placeholder="eg : Full Stack Developer" span={2} />
//                                 <Field label="Company / Organisation" value={company} onChange={setCompany} placeholder="eg : Google, Microsoft" span={2} />
//                             </div>

//                             <div style={s.field}>
//                                 <label style={s.label}>Short Bio</label>
//                                 <textarea
//                                     style={{ ...s.input, resize: "vertical", minHeight: "100px", paddingTop: "0.75rem" }}
//                                     placeholder="Tell people a little about yourself — your expertise, passion, or what you're working on..."
//                                     value={bio}
//                                     onChange={(e) => setBio(e.target.value)}
//                                     rows={4}
//                                 />
//                             </div>

//                             {/* Resume Upload */}
//                             <div style={s.resumeUploadArea}>
//                                 <div style={s.resumeIcon}>📄</div>
//                                 <div style={{ flex: 1 }}>
//                                     <p style={s.resumeTitle}>Resume / CV</p>
//                                     <p style={s.resumeHint}>{resumeName || "PDF or DOC — visitors can download it from your card"}</p>
//                                 </div>
//                                 <label htmlFor="resume-input" style={s.resumeBtn}>
//                                     {resumeName ? "Change" : "Upload"}
//                                 </label>
//                                 <input id="resume-input" type="file" accept=".pdf,.doc,.docx"
//                                     onChange={handleResumeChange} style={{ display: "none" }} />
//                             </div>
//                         </div>
//                     )}

//                     {/* STEP 2 — Links & Social */}
//                     {step === 2 && (
//                         <div style={s.stepContent}>
//                             <h2 style={s.stepHeading}>Links & Social Media</h2>
//                             <p style={s.stepSub}>Add your online presence. Toggle on what you want to show.</p>

//                             {/* Website */}
//                             <div style={s.linkRow}>
//                                 <div style={s.linkLeft}>
//                                     <div style={{ ...s.linkIcon, background: "#f0f0ff" }}>🌐</div>
//                                     <div>
//                                         <p style={s.linkLabel}>Website</p>
//                                         <p style={s.linkHint}>Your personal or business site</p>
//                                     </div>
//                                 </div>
//                                 <label style={s.toggle}>
//                                     <input type="checkbox" checked={websiteEnabled}
//                                         onChange={() => setWebsiteEnabled(v => !v)} style={{ display: "none" }} />
//                                     <div style={{ ...s.toggleTrack, background: websiteEnabled ? "#6366f1" : "#e5e7eb" }}>
//                                         <div style={{ ...s.toggleThumb, left: websiteEnabled ? "22px" : "2px" }} />
//                                     </div>
//                                 </label>
//                             </div>
//                             {websiteEnabled && (
//                                 <input style={{ ...s.input, marginBottom: "0.5rem" }}
//                                     placeholder="https://yourwebsite.com"
//                                     value={website}
//                                     onChange={(e) => setWebsite(e.target.value)} />
//                             )}

//                             <div style={s.divider} />

//                             {/* Social platforms */}
//                             {SOCIAL_PLATFORMS.map(({ key, label, placeholder, color, icon }) => (
//                                 <div key={key}>
//                                     <div style={s.linkRow}>
//                                         <div style={s.linkLeft}>
//                                             <div style={{ ...s.socialBadge, background: color }}>
//                                                 <span style={{ color: "#fff", fontSize: "0.7rem", fontWeight: "800" }}>{icon}</span>
//                                             </div>
//                                             <div>
//                                                 <p style={s.linkLabel}>{label}</p>
//                                             </div>
//                                         </div>
//                                         <label style={s.toggle}>
//                                             <input type="checkbox" checked={socials[key].enabled}
//                                                 onChange={() => toggleSocial(key)} style={{ display: "none" }} />
//                                             <div style={{ ...s.toggleTrack, background: socials[key].enabled ? color : "#e5e7eb" }}>
//                                                 <div style={{ ...s.toggleThumb, left: socials[key].enabled ? "22px" : "2px" }} />
//                                             </div>
//                                         </label>
//                                     </div>
//                                     {socials[key].enabled && (
//                                         <input style={{ ...s.input, marginBottom: "0.5rem" }}
//                                             placeholder={placeholder}
//                                             value={socials[key].url}
//                                             onChange={(e) => updateSocialUrl(key, e.target.value)} />
//                                     )}
//                                 </div>
//                             ))}
//                         </div>
//                     )}

//                     {/* STEP 3 — Review */}
//                     {step === 3 && (
//                         <div style={s.stepContent}>
//                             <h2 style={s.stepHeading}>Almost there! Review your card</h2>
//                             <p style={s.stepSub}>Everything looks good? Hit create and your card goes live instantly.</p>

//                             <div style={s.reviewGrid}>
//                                 <ReviewRow icon="👤" label="Name" value={name} />
//                                 <ReviewRow icon="📞" label="Phone" value={phone} />
//                                 <ReviewRow icon="✉️" label="Email" value={email} />
//                                 {location && <ReviewRow icon="📍" label="Location" value={location} />}
//                                 {designation && <ReviewRow icon="💼" label="Role" value={designation} />}
//                                 {company && <ReviewRow icon="🏢" label="Company" value={company} />}
//                                 {websiteEnabled && website && <ReviewRow icon="🌐" label="Website" value={website} />}
//                                 {resumeName && <ReviewRow icon="📄" label="Resume" value={resumeName} />}
//                                 {enabledSocials.length > 0 && (
//                                     <ReviewRow icon="🔗" label="Social Links" value={`${enabledSocials.length} platform${enabledSocials.length > 1 ? "s" : ""} added`} />
//                                 )}
//                             </div>

//                             {error && <p style={s.error}>{error}</p>}

//                             <div style={{ display: "flex", gap: "10px", marginBottom: "12px" }}>
//                                 <button onClick={() => setStep(2)} style={s.backStepBtn}>← Back</button>
//                                 <button onClick={handleSubmit} style={{ ...s.createBtn, margin: 0, flex: 1 }} disabled={loading}>
//                                     {loading ? <span>Creating your card...</span> : <span>🚀 Create My Digital Card</span>}
//                                 </button>
//                             </div>
//                             <p style={s.reviewNote}>Your card goes live immediately. You can edit it anytime.</p>
//                         </div>
//                     )}

//                     {/* Navigation */}
//                     {step < 3 && (
//                         <div style={s.navRow}>
//                             {step > 0 && (
//                                 <button onClick={() => setStep(s => s - 1)} style={s.backStepBtn}>← Back</button>
//                             )}
//                             <button
//                                 onClick={() => setStep(s => s + 1)}
//                                 style={{ ...s.nextBtn, opacity: canNext() ? 1 : 0.5 }}
//                                 disabled={!canNext()}
//                             >
//                                 {step === 2 ? "Review Card →" : "Continue →"}
//                             </button>
//                         </div>
//                     )}
//                 </div>
//             </div>

//             {/* Right — Live Preview */}
//             <div style={s.previewPanel}>
//                 <div style={s.previewInner}>
//                     {/* Theme Picker Strip */}
//                     <ThemeStrip selected={theme} onSelect={setTheme} />
//                     {/* Full Card Preview */}
//                     <style>{CARD_FONTS}</style>
//                     <CardRenderer
//                         themeId={theme}
//                         cardData={{
//                             name, phone, email, location, designation, company, bio,
//                             photoPreview, bannerPreview,
//                             website: websiteEnabled ? website : "",
//                             resumeUrl: resumeName ? "preview" : "",
//                             socials: Object.fromEntries(
//                                 enabledSocials.filter(p => socials[p.key]?.url).map(p => [p.key, socials[p.key].url])
//                             ),
//                         }}
//                     />

//                     <div style={{ paddingBottom: "32px" }} />
//                 </div>
//             </div>
//         </div>
//     );
// }

// function Field({ label, value, onChange, placeholder, type = "text", required, span = 1 }) {
//     return (
//         <div style={{ gridColumn: `span ${span}` }}>
//             <label style={s.label}>{label}</label>
//             <input style={s.input} type={type} placeholder={placeholder}
//                 value={value} onChange={(e) => onChange(e.target.value)} required={required} />
//         </div>
//     );
// }

// function ThemeStrip({ selected, onSelect }) {
//     const navigate = useNavigate();

//     const visibleThemes = (() => {
//         const selectedTheme = THEMES.find(t => t.id === selected);
//         const others = THEMES.filter(t => t.id !== selected);
//         if (selectedTheme) return [selectedTheme, others[0]].filter(Boolean);
//         return THEMES.slice(0, 2);
//     })();

//     return (
//         <div style={{ marginBottom: "12px" }}>
//             <div style={{ fontSize: "10px", fontWeight: "700", color: "rgba(255,255,255,0.4)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: "8px" }}>Card Theme</div>
//             <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
//                 {visibleThemes.map(t => {
//                     const isFree = FREE_THEMES.includes(t.id);
//                     const isSelected = selected === t.id;
//                     return (
//                         <div key={t.id} onClick={() => onSelect(t.id)} style={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}>
//                             <div style={{ width: "100%", height: "48px", borderRadius: "8px", background: t.bannerBg || t.headerBg || t.topBand || t.accentBar || t.cardBg, border: isSelected ? "2px solid #0AADA1" : "2px solid transparent", boxShadow: isSelected ? "0 0 0 2px rgba(10,173,161,0.3)" : "none", position: "relative", overflow: "hidden", transition: "border 0.15s" }}>
//                                 <div style={{ position: "absolute", bottom: "5px", left: "6px", width: "20px", height: "20px", borderRadius: "50%", background: t.avatarBg || t.avatarColor || "#fff", border: `1.5px solid ${t.avatarBorder || t.accent || t.accentColor || "#fff"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "6px", fontWeight: "800", color: t.avatarColor || "#fff" }}>AM</div>
//                                 {!isFree && <div style={{ position: "absolute", top: "4px", right: "5px", fontSize: "8px", color: "#f59e0b" }}>★</div>}
//                             </div>
//                             <div style={{ fontSize: "9px", fontWeight: "600", color: isSelected ? "#0AADA1" : "rgba(255,255,255,0.4)", textAlign: "center", lineHeight: 1.2, width: "100%" }}>{t.name.split(" ")[0]}</div>
//                         </div>
//                     );
//                 })}

//                 {/* 200+ more tile */}
//                 <div
//                     onClick={() => navigate("/themes", { state: { targetPath: "/create", confirmContext: "create", preselectedTheme: selected } })}
//                     style={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}
//                 >
//                     <div style={{ width: "100%", height: "48px", borderRadius: "8px", background: "rgba(10,173,161,0.08)", border: "2px dashed rgba(10,173,161,0.4)", display: "flex", alignItems: "center", justifyContent: "center", transition: "border 0.15s" }}>
//                         <span style={{ fontSize: "16px", color: "#0AADA1", lineHeight: 1 }}>+</span>
//                     </div>
//                     <div style={{ fontSize: "9px", fontWeight: "600", color: "rgba(10,173,161,0.8)", textAlign: "center", lineHeight: 1.2, width: "100%" }}>200+ more</div>
//                 </div>
//             </div>
//         </div>
//     );
// }

// function ReviewRow({ icon, label, value }) {
//     return (
//         <div style={s.reviewRow}>
//             <span style={s.reviewIcon}>{icon}</span>
//             <div>
//                 <p style={s.reviewLabel}>{label}</p>
//                 <p style={s.reviewValue}>{value}</p>
//             </div>
//         </div>
//     );
// }

// function GoogleIcon() {
//     return (
//         <svg width="18" height="18" viewBox="0 0 24 24">
//             <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
//             <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
//             <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
//             <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
//         </svg>
//     );
// }

// function CameraIcon() {
//     return (
//         <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
//             <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
//             <circle cx="12" cy="13" r="4" />
//         </svg>
//     );
// }

// const s = {
//     page: {
//         minHeight: "100vh",
//         display: "grid",
//         gridTemplateColumns: "1fr 420px",
//         fontFamily: "'Segoe UI', Arial, sans-serif",
//     },

//     // FORM PANEL
//     formPanel: {
//         background: "#fff",
//         display: "flex", flexDirection: "column",
//         borderRight: "1px solid #f3f4f6",
//         minHeight: "100vh",
//     },
//     topBar: {
//         display: "flex", alignItems: "center", justifyContent: "space-between",
//         padding: "1.25rem 2rem", borderBottom: "1px solid #f3f4f6",
//     },
//     backBtn: {
//         background: "none", border: "none", color: "#6b7280",
//         fontSize: "0.9rem", fontWeight: "600", cursor: "pointer", padding: 0,
//     },
//     topLogo: {
//         fontSize: "1rem", fontWeight: "800", color: "#1a1a2e",
//         display: "flex", alignItems: "center", gap: "5px",
//     },
//     logoDot: { color: "#6366f1", fontSize: "0.75rem" },

//     // STEP BAR
//     stepBar: {
//         display: "flex", alignItems: "flex-start",
//         padding: "1.5rem 2rem",
//         borderBottom: "1px solid #f3f4f6",
//         gap: 0,
//     },
//     stepItem: {
//         display: "flex", flexDirection: "column", alignItems: "center",
//         position: "relative", flex: 1,
//     },
//     stepCircle: {
//         width: "32px", height: "32px", borderRadius: "50%",
//         display: "flex", alignItems: "center", justifyContent: "center",
//         fontSize: "0.8rem", fontWeight: "800",
//         transition: "all 0.3s", zIndex: 1,
//     },
//     stepLabel: {
//         fontSize: "0.7rem", marginTop: "6px", textAlign: "center",
//         transition: "all 0.3s", whiteSpace: "nowrap",
//     },
//     stepLine: {
//         position: "absolute", top: "16px", left: "calc(50% + 16px)",
//         width: "calc(100% - 32px)", height: "2px",
//         transition: "background 0.3s",
//     },

//     // FORM BODY
//     formBody: {
//         flex: 1, padding: "2rem",
//         overflowY: "auto",
//     },
//     stepContent: { maxWidth: "560px", margin: "0 auto" },
//     stepHeading: { fontSize: "1.5rem", fontWeight: "900", color: "#1a1a2e", margin: "0 0 0.4rem" },
//     stepSub: { fontSize: "0.9rem", color: "#6b7280", margin: "0 0 2rem", lineHeight: 1.6 },

//     // AUTO-FILL
//     autoFillCard: {
//         position: "relative", borderRadius: "16px", padding: "18px 20px",
//         border: "1.5px solid rgba(10,173,161,0.3)", marginBottom: "1.5rem",
//         transition: "all 0.25s cubic-bezier(0.22,1,0.36,1)", overflow: "hidden",
//         cursor: "default",
//     },
//     autoFillTopBar: {
//         position: "absolute", top: 0, left: "10%", right: "10%", height: "2px",
//         background: "linear-gradient(90deg,transparent,#0AADA1,#6366f1,transparent)",
//         borderRadius: "2px",
//     },
//     autoFillIcon: {
//         width: "48px", height: "48px", borderRadius: "14px", flexShrink: 0,
//         background: "linear-gradient(135deg,rgba(10,173,161,0.15),rgba(99,102,241,0.1))",
//         border: "1px solid rgba(10,173,161,0.2)",
//         display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px",
//     },
//     autoFillTitle: { fontSize: "14px", fontWeight: "800", color: "#1a1a2e", marginBottom: "3px" },
//     autoFillSub: { fontSize: "12px", color: "#6b7280", lineHeight: 1.5 },
//     autoFillBtn: {
//         padding: "9px 18px", borderRadius: "10px", border: "none", flexShrink: 0,
//         background: "linear-gradient(135deg,#0C9189,#0AADA1)", color: "#fff",
//         fontSize: "12px", fontWeight: "700", cursor: "pointer", whiteSpace: "nowrap",
//         boxShadow: "0 4px 12px rgba(10,173,161,0.3)",
//         fontFamily: "'Segoe UI', Arial, sans-serif",
//     },
//     autoFillScanLabel: { fontSize: "13px", fontWeight: "700", color: "#1a1a2e", marginBottom: "12px" },
//     autoFillScanBar: {
//         height: "4px", borderRadius: "2px", background: "rgba(10,173,161,0.15)",
//         overflow: "hidden", margin: "0 auto 8px", maxWidth: "300px",
//     },
//     autoFillScanFill: {
//         height: "100%", borderRadius: "2px", width: "40%",
//         background: "linear-gradient(90deg,#0AADA1,#6366f1)",
//         animation: "scan-slide 1.2s ease-in-out infinite alternate",
//     },
//     autoFillScanSub: { fontSize: "11px", color: "#9ca3af" },
//     autoFillSuccessIcon: {
//         width: "40px", height: "40px", borderRadius: "50%", flexShrink: 0,
//         background: "#22c55e", color: "#fff",
//         display: "flex", alignItems: "center", justifyContent: "center",
//         fontSize: "18px", fontWeight: "800",
//         boxShadow: "0 4px 12px rgba(34,197,94,0.35)",
//     },
//     autoFillSuccessTitle: { fontSize: "14px", fontWeight: "800", color: "#15803d", marginBottom: "2px" },
//     autoFillSuccessSub: { fontSize: "12px", color: "#6b7280" },
//     autoFillReparse: {
//         padding: "7px 14px", borderRadius: "8px", flexShrink: 0,
//         border: "1px solid rgba(34,197,94,0.3)", background: "transparent",
//         fontSize: "11px", fontWeight: "700", color: "#15803d", cursor: "pointer",
//         fontFamily: "'Segoe UI', Arial, sans-serif",
//     },

//     // PHOTO
//     photoArea: {
//         display: "flex", alignItems: "center", gap: "1.5rem",
//         background: "#f8f9ff", borderRadius: "16px",
//         padding: "1.25rem 1.5rem", marginBottom: "1.5rem",
//         border: "1.5px solid #e5e7eb",
//     },
//     avatarWrap: { position: "relative", width: "72px", height: "72px", flexShrink: 0 },
//     avatarImg: { width: "72px", height: "72px", borderRadius: "50%", objectFit: "cover", border: "3px solid #e5e7eb" },
//     avatarInitials: {
//         width: "72px", height: "72px", borderRadius: "50%",
//         background: "linear-gradient(135deg, #1a1a2e, #0f3460)",
//         color: "#fff", display: "flex", alignItems: "center",
//         justifyContent: "center", fontSize: "1.4rem", fontWeight: "700",
//     },
//     cameraBtn: {
//         position: "absolute", bottom: "0px", right: "0px",
//         width: "26px", height: "26px", borderRadius: "50%",
//         background: "#6366f1", display: "flex", alignItems: "center",
//         justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 6px rgba(99,102,241,0.4)",
//     },
//     photoTitle: { fontWeight: "700", fontSize: "0.9rem", color: "#1a1a2e", margin: "0 0 0.2rem" },
//     photoHint: { fontSize: "0.78rem", color: "#9ca3af", margin: "0 0 0.6rem" },
//     photoUploadBtn: {
//         display: "inline-block", padding: "0.4rem 1rem",
//         background: "#f3f4f6", border: "1.5px solid #e5e7eb",
//         borderRadius: "8px", fontSize: "0.82rem", fontWeight: "600",
//         color: "#374151", cursor: "pointer",
//     },
//     bannerArea: {
//         display: "flex", alignItems: "flex-start", gap: "1rem",
//         background: "#f9fafb", borderRadius: "14px",
//         padding: "1rem", border: "1.5px dashed #e5e7eb", marginBottom: "1rem",
//     },
//     bannerPreviewBox: {
//         width: "120px", height: "52px", borderRadius: "8px",
//         background: "#e5e7eb", overflow: "hidden", flexShrink: 0,
//         display: "flex", alignItems: "center", justifyContent: "center",
//     },
//     bannerImg: { width: "100%", height: "100%", objectFit: "cover" },
//     bannerPlaceholder: { fontSize: "0.7rem", color: "#9ca3af", textAlign: "center" },

//     // FIELDS
//     fieldGrid: {
//         display: "grid", gridTemplateColumns: "1fr 1fr",
//         gap: "1rem", marginBottom: "1rem",
//     },
//     field: { display: "flex", flexDirection: "column", gap: "0.35rem", marginBottom: "1rem" },
//     label: { fontSize: "0.8rem", fontWeight: "700", color: "#374151", marginBottom: "0.35rem", display: "block" },
//     input: {
//         padding: "0.75rem 1rem", border: "1.5px solid #e5e7eb",
//         borderRadius: "10px", fontSize: "0.92rem", outline: "none",
//         fontFamily: "inherit", width: "100%", boxSizing: "border-box",
//         transition: "border-color 0.2s",
//     },

//     // RESUME
//     resumeUploadArea: {
//         display: "flex", alignItems: "center", gap: "1rem",
//         background: "#f8f9ff", borderRadius: "14px",
//         padding: "1rem 1.25rem", border: "1.5px dashed #d1d5db",
//         marginTop: "1rem",
//     },
//     resumeIcon: { fontSize: "1.5rem" },
//     resumeTitle: { fontWeight: "700", fontSize: "0.88rem", color: "#1a1a2e", margin: "0 0 0.2rem" },
//     resumeHint: { fontSize: "0.78rem", color: "#9ca3af", margin: 0 },
//     resumeBtn: {
//         padding: "0.5rem 1rem", background: "#6366f1",
//         color: "#fff", border: "none", borderRadius: "8px",
//         fontSize: "0.82rem", fontWeight: "700", cursor: "pointer",
//         flexShrink: 0, whiteSpace: "nowrap",
//     },

//     // LINKS & SOCIAL
//     linkRow: {
//         display: "flex", alignItems: "center", justifyContent: "space-between",
//         padding: "0.75rem 0",
//     },
//     linkLeft: { display: "flex", alignItems: "center", gap: "0.75rem" },
//     linkIcon: {
//         width: "38px", height: "38px", borderRadius: "10px",
//         display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem",
//     },
//     socialBadge: {
//         width: "38px", height: "38px", borderRadius: "10px",
//         display: "flex", alignItems: "center", justifyContent: "center",
//     },
//     linkLabel: { fontWeight: "700", fontSize: "0.9rem", color: "#1a1a2e", margin: 0 },
//     linkHint: { fontSize: "0.75rem", color: "#9ca3af", margin: 0 },
//     toggle: { cursor: "pointer", position: "relative" },
//     toggleTrack: {
//         width: "44px", height: "24px", borderRadius: "50px",
//         position: "relative", transition: "background 0.25s",
//     },
//     toggleThumb: {
//         width: "20px", height: "20px", borderRadius: "50%",
//         background: "#fff", position: "absolute", top: "2px",
//         transition: "left 0.25s", boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
//     },
//     divider: { height: "1px", background: "#f3f4f6", margin: "0.5rem 0" },

//     // REVIEW
//     reviewGrid: { display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "2rem" },
//     reviewRow: {
//         display: "flex", alignItems: "center", gap: "1rem",
//         background: "#f8f9ff", borderRadius: "12px",
//         padding: "0.75rem 1rem", border: "1px solid #e5e7eb",
//     },
//     reviewIcon: { fontSize: "1.2rem", width: "28px", textAlign: "center" },
//     reviewLabel: { fontSize: "0.72rem", fontWeight: "600", color: "#9ca3af", margin: "0 0 0.1rem", textTransform: "uppercase", letterSpacing: "0.5px" },
//     reviewValue: { fontSize: "0.9rem", fontWeight: "600", color: "#1a1a2e", margin: 0 },

//     // NAVIGATION
//     navRow: {
//         display: "flex", justifyContent: "space-between", alignItems: "center",
//         marginTop: "2rem", paddingTop: "1.5rem", borderTop: "1px solid #f3f4f6",
//     },
//     backStepBtn: {
//         background: "none", border: "1.5px solid #e5e7eb",
//         borderRadius: "10px", padding: "0.75rem 1.5rem",
//         fontSize: "0.9rem", fontWeight: "600", color: "#6b7280", cursor: "pointer",
//     },
//     nextBtn: {
//         marginLeft: "auto",
//         padding: "0.75rem 2rem",
//         background: "linear-gradient(135deg, #6366f1, #4f46e5)",
//         color: "#fff", border: "none", borderRadius: "10px",
//         fontSize: "0.95rem", fontWeight: "700", cursor: "pointer",
//         boxShadow: "0 4px 16px rgba(99,102,241,0.3)",
//     },
//     createBtn: {
//         width: "100%", padding: "1rem",
//         background: "linear-gradient(135deg, #6366f1, #4f46e5)",
//         color: "#fff", border: "none", borderRadius: "12px",
//         fontSize: "1.05rem", fontWeight: "800", cursor: "pointer",
//         boxShadow: "0 8px 24px rgba(99,102,241,0.35)",
//         marginBottom: "0.75rem",
//     },
//     reviewNote: { textAlign: "center", fontSize: "0.8rem", color: "#9ca3af", margin: 0 },
//     error: { color: "#ef4444", fontSize: "0.875rem", margin: "0 0 1rem", textAlign: "center" },

//     // AUTH
//     authPage: { minHeight: "100vh", background: "linear-gradient(135deg, #0f0c29 0%, #1a1a2e 50%, #0f3460 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", fontFamily: "'Segoe UI', Arial, sans-serif" },
//     authBox: { background: "#fff", borderRadius: "24px", padding: "3rem 2.5rem", width: "100%", maxWidth: "420px", textAlign: "center", boxShadow: "0 40px 80px rgba(0,0,0,0.5)" },
//     authLogo: { fontSize: "1.1rem", fontWeight: "800", color: "#1a1a2e", display: "inline-flex", alignItems: "center", gap: "6px", marginBottom: "1.5rem" },
//     authTitle: { fontSize: "1.6rem", fontWeight: "900", color: "#1a1a2e", margin: "0 0 0.75rem" },
//     authSub: { fontSize: "0.9rem", color: "#6b7280", margin: "0 0 2rem", lineHeight: 1.6 },
//     googleBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem", width: "100%", padding: "0.9rem", background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: "12px", fontSize: "0.95rem", fontWeight: "700", color: "#1a1a2e", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", marginBottom: "1rem" },
//     authNote: { fontSize: "0.78rem", color: "#9ca3af", margin: 0 },

//     // SLUG
//     slugWrap: { display: "flex", alignItems: "center", border: "1.5px solid #e5e7eb", borderRadius: "10px", overflow: "hidden" },
//     slugBase: { background: "#f3f4f6", padding: "0.75rem 0.75rem", fontSize: "0.82rem", color: "#6b7280", fontWeight: "600", whiteSpace: "nowrap", borderRight: "1.5px solid #e5e7eb" },
//     slugInput: { flex: 1, padding: "0.75rem 0.75rem", border: "none", outline: "none", fontSize: "0.92rem", fontFamily: "inherit" },
//     slugStatus: { marginTop: "0.4rem", fontSize: "0.78rem", lineHeight: 1.5 },
//     slugAvailable: { color: "#16a34a" },
//     slugTaken: { color: "#dc2626" },
//     slugChecking: { color: "#9ca3af" },

//     // TABS
//     themePicker: {
//         display: "flex", alignItems: "center", justifyContent: "space-between",
//         marginBottom: "0.75rem",
//         background: "rgba(255,255,255,0.06)",
//         borderRadius: "12px", padding: "8px 12px",
//         border: "1px solid rgba(255,255,255,0.08)",
//     },
//     themeLabel: { fontSize: "0.72rem", fontWeight: "700", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "1px" },
//     themeOptions: { display: "flex", gap: "4px" },
//     themeBtn: {
//         padding: "4px 12px", border: "1px solid rgba(255,255,255,0.12)",
//         borderRadius: "8px", background: "transparent",
//         color: "rgba(255,255,255,0.45)", fontSize: "0.75rem",
//         fontWeight: "600", cursor: "pointer",
//     },
//     themeBtnActive: {
//         background: "#fff", color: "#1a1a2e",
//         border: "1px solid #fff",
//     },
//     tabRow: {
//         display: "flex", gap: "0.5rem",
//         marginBottom: "1rem",
//         background: "rgba(255,255,255,0.08)",
//         borderRadius: "12px", padding: "4px",
//     },
//     tabBtn: {
//         flex: 1, padding: "0.5rem",
//         background: "transparent", border: "none",
//         borderRadius: "9px", fontSize: "0.78rem",
//         fontWeight: "600", color: "rgba(255,255,255,0.5)",
//         cursor: "pointer", transition: "all 0.2s",
//     },
//     tabBtnActive: {
//         background: "#fff",
//         color: "#1a1a2e",
//         boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
//     },

//     // PREVIEW PANEL
//     previewPanel: {
//         background: "linear-gradient(160deg, #0f0c29 0%, #1a1a2e 50%, #0f3460 100%)",
//         padding: "2rem 1.5rem",
//         display: "flex", flexDirection: "column", alignItems: "center",
//         position: "sticky", top: 0, height: "100vh", overflowY: "auto",
//     },
//     previewInner: { width: "100%", maxWidth: "420px" },
//     previewLabel: {
//         fontSize: "0.7rem", fontWeight: "700", color: "rgba(255,255,255,0.4)",
//         textTransform: "uppercase", letterSpacing: "2px",
//         textAlign: "center", marginBottom: "1.25rem",
//     },

//     // PREVIEW CARD
//     previewCard: {
//         background: "#fff", borderRadius: "20px",
//         overflow: "hidden",
//         boxShadow: "0 30px 60px rgba(0,0,0,0.5)",
//     },
//     previewHeader: {
//         background: "linear-gradient(135deg, #1a1a2e, #0f3460)",
//         padding: "1.5rem 1.25rem 1.25rem",
//         textAlign: "center",
//     },
//     previewAvatarWrap: { marginBottom: "0.75rem" },
//     previewAvatarImg: {
//         width: "56px", height: "56px", borderRadius: "50%",
//         objectFit: "cover", border: "3px solid rgba(255,255,255,0.3)",
//         display: "block", margin: "0 auto",
//     },
//     previewAvatarInitials: {
//         width: "56px", height: "56px", borderRadius: "50%",
//         background: "rgba(255,255,255,0.15)", border: "3px solid rgba(255,255,255,0.3)",
//         color: "#fff", display: "flex", alignItems: "center",
//         justifyContent: "center", fontSize: "1.1rem", fontWeight: "800",
//         margin: "0 auto",
//     },
//     previewName: { color: "#fff", fontSize: "1rem", fontWeight: "800", margin: "0 0 0.25rem" },
//     previewRole: { color: "rgba(255,255,255,0.75)", fontSize: "0.72rem", margin: "0 0 0.2rem" },
//     previewLocation: { color: "rgba(255,255,255,0.55)", fontSize: "0.68rem", margin: 0 },

//     previewSection: { padding: "0.75rem 1rem", borderBottom: "1px solid #f3f4f6" },
//     previewRow: {
//         display: "flex", alignItems: "center", gap: "0.5rem",
//         fontSize: "0.75rem", color: "#374151", padding: "0.2rem 0",
//     },
//     previewBio: { padding: "0.75rem 1rem", borderBottom: "1px solid #f3f4f6" },
//     previewBioText: { fontSize: "0.72rem", color: "#6b7280", margin: 0, lineHeight: 1.5 },

//     previewSocials: { display: "flex", gap: "5px", flexWrap: "wrap", marginTop: "0.4rem" },
//     previewSocialDot: {
//         width: "22px", height: "22px", borderRadius: "6px",
//         display: "flex", alignItems: "center", justifyContent: "center",
//     },

//     previewQr: {
//         display: "flex", flexDirection: "column", alignItems: "center",
//         padding: "0.75rem", borderBottom: "1px solid #f3f4f6",
//     },
//     previewQrBox: {
//         background: "#f9fafb", padding: "8px", borderRadius: "6px",
//         border: "1px solid #e5e7eb",
//     },
//     previewQrText: { fontSize: "0.62rem", color: "#9ca3af", marginTop: "5px" },

//     previewFooter: { display: "flex", gap: "0.5rem", padding: "0.75rem" },
//     previewSaveBtn: {
//         flex: 1, background: "linear-gradient(135deg, #1a1a2e, #0f3460)",
//         color: "#fff", borderRadius: "8px", padding: "0.5rem",
//         fontSize: "0.68rem", fontWeight: "700", textAlign: "center",
//     },
//     previewWalletBtn: {
//         flex: 1, background: "#0F9D58",
//         color: "#fff", borderRadius: "8px", padding: "0.5rem",
//         fontSize: "0.68rem", fontWeight: "700", textAlign: "center",
//     },

// };
